import React, { useState, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEyeSlash,
  faCalendarAlt,
  faCloudUploadAlt,
  faPlus,
  faTrash,
  faTimes,
  faSave,
  faClipboardList,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import html2pdf from 'html2pdf.js';

// Ensure these paths match your project structure
import { fetchSystemCaregivers, fetchPracticeProviders, fetchPracticeCaregivers, fetchPractice } from '../../../../store/slices/practiceSlice';
import { createPatient, updatePatient, setCurrentPatient } from '../../../../store/slices/patientSlice';

import { toast } from 'react-toastify';
import useEmailNotifications from '../../../../hooks/useEmailNotifications';
import inventoryService from '../../../../services/inventoryService';
import patientService from '../../../../services/patientService';
import practiceService from '../../../../services/practiceService';
import { TIME_ZONES, US_STATES, TITLES, normalizeTimeZone } from '../../../../utils/constants';
import { buildPatientProfileImageUrl } from '../../../../utils/patientProfileImage.js';
import { PatientAscStyleSinglePicker, PatientAscStyleMultiPicker } from '../shared/PatientCaregiverPickers';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import ActionButtons from '../../../../components/Common/ActionButtons/ActionButtons';
import PatientsLogModal from '../../../../components/PatientsLogModal/PatientsLogModal';
import FormActionButtons from '../../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';
import './AddPatient.css';
import '../../PracticesList/PracticesList.css';
import { validatePassword } from '../../../../utils/passwordValidation';
import { resolvePracticeLogoUrl } from '../../../../utils/assetUrls';

/**
 * AddPatient — `/practices/:practiceId/add-patient`
 * - Create: full intake form.
 * - Edit (from patient list pencil): `?editId=<patients.id>` — same single-page form, not the tabbed PatientEditView.
 *
 * For the tabbed workspace (profile, schedule, meters, documents), open **Edit patient** from the patient profile page (`/patients/:patientId/edit`).
 */

/** Max bytes per document (aligned with multer). Client rejects at >= limit so every file is strictly under 10 MiB. */
const MAX_DOCUMENT_FILE_BYTES = 10 * 1024 * 1024;
const DOCUMENT_FILE_SIZE_ERROR = 'Each file must be under 10 MB.';

function isFileAtOrOverDocumentLimit(file) {
  return file instanceof File && file.size >= MAX_DOCUMENT_FILE_BYTES;
}

/**
 * Inline validation for document rows (oversize attempt, attached file size, display name).
 * Used by useEffect (live, before submit) and validateForm.
 */
function buildDocumentFieldErrors(documents) {
  const out = {};
  documents.forEach((doc, idx) => {
    const fk = `documentFile_${idx}`;
    const nk = `documentFileName_${idx}`;
    const oversized =
      Boolean(doc?.fileTooLargeName) || isFileAtOrOverDocumentLimit(doc?.file);
    if (oversized) {
      out[fk] = DOCUMENT_FILE_SIZE_ERROR;
    }
    const fn = (doc?.fileName || '').trim();
    if (doc?.file && !fn) {
      out[nk] = 'Enter a file name for each uploaded document';
    } else if (fn && !/^[a-zA-Z0-9 ]+$/.test(fn)) {
      out[nk] = 'File name: letters, numbers, and spaces only';
    }
  });
  return out;
}

/** Axios GET /documents — body is `{ success, data: Row[] }`. */
function extractDocumentsRowsFromResponse(res) {
  const body = res?.data;
  if (!body) return [];
  if (body.success === false) return [];
  if (Array.isArray(body.data)) return body.data;
  return [];
}

function emptyAddPatientFormState() {
  return {
    firstName: '', middleInitial: '', lastName: '', addressLine1: '', addressLine2: '',
    city: '', state: '', stateId: null, zipCode: '', homePhone: '', cellPhone: '', fax: '',
    email: '', gender: 'Male', dateOfBirth: '', twoWayAuth: 'Disabled', status: 'Active', timeZone: '',
    rpmStartDate: '', provider: '',
    username: '', password: '', confirmPassword: '', title: '',
    systemCaregiverIds: [], practiceCaregiverIds: [], profilePicture: null
  };
}

/** Practice `selected_monitoring_types` may be number[], string[], or a JSON string — normalize for `.includes(1)` etc. */
function normalizePracticeMonitoringTypes(raw) {
  if (raw == null || raw === '') return [];
  let arr = raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    try {
      arr = JSON.parse(s);
    } catch {
      arr = s.split(/[\s,]+/).filter(Boolean);
    }
  }
  if (!Array.isArray(arr)) return [];
  return [...new Set(arr.map((x) => parseInt(String(x), 10)).filter((n) => !Number.isNaN(n) && n > 0))];
}

/** Console debug: filter DevTools by `[AddPatient]` */
function sanitizeAddPatientLogPayload(obj) {
  if (!obj || typeof obj !== 'object' || obj instanceof FormData) return obj;
  try {
    const out = { ...obj };
    if (out.password) out.password = '***';
    if (out.confirmPassword) out.confirmPassword = '***';
    if (out.profilePicture instanceof File) {
      out.profilePicture = `[File: ${out.profilePicture.name}, ${out.profilePicture.size} bytes]`;
    }
    if (Array.isArray(out.documents)) {
      out.documents = out.documents.map((d) => ({
        fileName: d.fileName,
        fileTooLargeName: d.fileTooLargeName,
        file: d.file instanceof File ? `[File: ${d.file.name}]` : d.file
      }));
    }
    return out;
  } catch {
    return obj;
  }
}

function formDataToLogObject(fd) {
  const out = {};
  try {
    const pairs = [...fd.entries()];
    for (const [k, v] of pairs) {
      const val = v instanceof File ? `[File: ${v.name}, ${v.size} bytes]` : v;
      if (out[k] !== undefined) {
        out[k] = Array.isArray(out[k]) ? [...out[k], val] : [out[k], val];
      } else {
        out[k] = val;
      }
    }
    out._entryCount = pairs.length;
  } catch (e) {
    return { error: 'could not read FormData', detail: String(e?.message || e) };
  }
  return out;
}

function emptyMeterMappingRow() {
  return {
    productId: null,
    modelNumber: '',
    extension: '',
    serialNumber: '',
    gatewaySerialNumber: '',
    availableSerials: [],
    serialFetchLoading: false,
    serialSource: 'custom',
    serialHydratedFromList: true
  };
}

/** Same extension choices as Inventory Details → Add new meter mapping */
const METER_EXTENSION_OPTIONS = [
  { value: '1', label: 'User 1' },
  { value: '2', label: 'User 2' },
  { value: '3', label: 'User 3' },
  { value: '4', label: 'User 4' },
  { value: 'A', label: 'All' }
];

function normalizeMeterExtensionValue(ext) {
  const s = String(ext ?? '').trim();
  if (!s) return '';
  if (METER_EXTENSION_OPTIONS.some((o) => String(o.value) === s)) return s;
  return '';
}

function isValidEmailFormat(email) {
  const s = String(email ?? '').trim();
  if (!s) return false;
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(s);
}

function sanitizeCity(value) {
  return String(value ?? '').replace(/[^a-zA-Z\s'-]/g, '');
}

function sanitizeInsuranceTier(row) {
  return {
    insuranceName: String(row?.insuranceName ?? row?.insurance_name ?? '').replace(/[^a-zA-Z\s]/g, ''),
    subscriberId: String(row?.subscriberId ?? row?.subscriber_id ?? '').replace(/[^a-zA-Z0-9]/g, ''),
    insuredName: String(row?.insuredName ?? row?.insured_name ?? '').replace(/[^a-zA-Z\s'-]/g, ''),
    groupNo: String(row?.groupNo ?? row?.group_no ?? '').replace(/[^a-zA-Z0-9]/g, '')
  };
}

function sanitizeInsuranceDataFromApi(insurance) {
  if (!insurance) {
    return {
      primary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' },
      secondary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' }
    };
  }
  return {
    primary: sanitizeInsuranceTier(insurance.primary || {}),
    secondary: sanitizeInsuranceTier(insurance.secondary || {})
  };
}

/** Map API / table row → inline form row (`id` when saved, for keys and sync). */
function mapApiMeterToFormRow(m) {
  if (!m) return emptyMeterMappingRow();
  const pid = m.product_id ?? m.productId;
  return {
    ...(m.id != null ? { id: m.id } : {}),
    ...(pid != null && pid !== ''
      ? (() => {
          const n = parseInt(String(pid), 10);
          return Number.isFinite(n) ? { productId: n } : {};
        })()
      : {}),
    modelNumber: m.model_number || m.modelNumber || '',
    extension: normalizeMeterExtensionValue(m.extension ?? ''),
    serialNumber: m.serial_number || m.serialNumber || '',
    gatewaySerialNumber: m.gateway_serial_number || m.gatewaySerialNumber || '',
    availableSerials: [],
    serialFetchLoading: false,
    serialSource: 'custom',
    serialHydratedFromList: true
  };
}

function isCompleteMeterFormRow(m) {
  return (
    String(m?.modelNumber || '').trim() !== '' &&
    String(m?.extension || '').trim() !== '' &&
    String(m?.serialNumber || '').trim() !== ''
  );
}

function trimMeterSubmitRow(m) {
  const out = {
    modelNumber: String(m.modelNumber || '').trim(),
    extension: String(m.extension || '').trim(),
    serialNumber: String(m.serialNumber || '').trim(),
    gatewaySerialNumber: String(m.gatewaySerialNumber || '').trim()
  };
  if (m.productId != null && Number.isFinite(Number(m.productId))) {
    out.productId = Number(m.productId);
  }
  return out;
}

function patientListMeterToSubmitRow(row) {
  return trimMeterSubmitRow({
    modelNumber: row.model_number || row.modelNumber,
    extension: row.extension,
    serialNumber: row.serial_number || row.serialNumber,
    gatewaySerialNumber: row.gateway_serial_number || row.gatewaySerialNumber
  });
}

function isCompleteSubmitRow(r) {
  return !!(r.modelNumber && r.extension && r.serialNumber);
}

function dedupeMeterSubmitRows(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows) {
    if (!isCompleteSubmitRow(r)) continue;
    const k = `${r.modelNumber}|${r.extension}|${r.serialNumber}`.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

/** Browser cache for consent form (signature dates, etc.) when the patient API does not return them. */
const PATIENT_CONSENT_STORAGE_PREFIX = 'rpm_patient_consent_v1';

function patientConsentStorageKey(practiceId, patientTableId) {
  if (practiceId == null || patientTableId == null) return null;
  return `${PATIENT_CONSENT_STORAGE_PREFIX}:${practiceId}:${patientTableId}`;
}

function savePatientConsentDraft(practiceId, patientTableId, consent) {
  const key = patientConsentStorageKey(practiceId, patientTableId);
  if (!key || !consent || typeof consent !== 'object') return;
  try {
    localStorage.setItem(key, JSON.stringify(consent));
  } catch (e) {
    console.warn('[AddPatient] Could not cache consent form in localStorage:', e?.message || e);
  }
}

function readPatientConsentDraft(practiceId, patientTableId) {
  const key = patientConsentStorageKey(practiceId, patientTableId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw || !String(raw).trim().startsWith('{')) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const PATIENT_CONSENT_PAYLOAD_KEYS = [
  'name', 'dob', 'tel', 'address', 'email',
  'patientSignature', 'patientDate', 'clinicianSignature', 'clinicianDate',
  'caregiverName', 'caregiverSignature', 'caregiverDate'
];

/** If the backend includes consent on the patient payload, normalize it to a plain object. */
function parsePatientConsentFromPayload(patient) {
  if (!patient || typeof patient !== 'object') return null;
  const raw =
    patient.consent_data ??
    patient.consentData ??
    patient.consent_form_snapshot ??
    patient.consentFormSnapshot;
  let obj = raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s || (!s.startsWith('{') && !s.startsWith('['))) return null;
    try {
      obj = JSON.parse(s);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const hasKnown = PATIENT_CONSENT_PAYLOAD_KEYS.some((k) => Object.prototype.hasOwnProperty.call(obj, k));
  if (!hasKnown) return null;
  return obj;
}

function buildEditModeConsentFormState({ patient, dobValue, homeDigits, formatPhoneNumberFn, fromApi, fromStorage }) {
  const todayStr = new Date().toISOString().split('T')[0];
  const demographicFallback = {
    name: `${patient.first_name || ''} ${patient.last_name || ''}`.trim(),
    dob: dobValue,
    tel: homeDigits ? formatPhoneNumberFn(homeDigits) : '',
    address: patient.address_line_1 || patient.address?.address_line_1 || '',
    email: patient.email || ''
  };
  const stored = fromStorage && typeof fromStorage === 'object' && !Array.isArray(fromStorage) ? fromStorage : {};
  const api = fromApi && typeof fromApi === 'object' && !Array.isArray(fromApi) ? fromApi : {};
  const merged = {
    patientSignature: '',
    patientDate: '',
    clinicianSignature: '',
    clinicianDate: '',
    caregiverName: '',
    caregiverSignature: '',
    caregiverDate: '',
    ...stored,
    ...api,
    ...demographicFallback
  };
  if (!merged.patientDate) merged.patientDate = todayStr;
  if (!merged.clinicianDate) merged.clinicianDate = todayStr;
  return merged;
}

function normalizePatientUpdatePayloadForDiff(payload) {
  const p = payload || {};
  const normalizeIdArray = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((n) => parseInt(String(n), 10))
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b);

  const normalizeMonitoring = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((m) => ({
        type: String(m?.type || '').trim(),
        icdCode: String(m?.icdCode ?? m?.icd_code ?? '').trim()
      }))
      .filter((m) => m.type)
      .sort((a, b) => a.type.localeCompare(b.type));

  const normalizeMeters = (arr) =>
    dedupeMeterSubmitRows(Array.isArray(arr) ? arr : [])
      .map((m) => {
        const row = {
          modelNumber: String(m.modelNumber || '').trim(),
          extension: String(m.extension || '').trim(),
          serialNumber: String(m.serialNumber || '').trim(),
          gatewaySerialNumber: String(m.gatewaySerialNumber || '').trim()
        };
        if (m.productId != null && Number.isFinite(Number(m.productId))) {
          row.productId = Number(m.productId);
        }
        return row;
      })
      .sort((a, b) =>
        `${a.modelNumber}|${a.extension}|${a.serialNumber}`.localeCompare(
          `${b.modelNumber}|${b.extension}|${b.serialNumber}`
        )
      );

  return {
    ...p,
    practice_caregiver_ids: normalizeIdArray(p.practice_caregiver_ids),
    system_caregiver_ids: normalizeIdArray(p.system_caregiver_ids),
    monitoring_types: normalizeMonitoring(p.monitoring_types),
    meter_mappings: normalizeMeters(p.meter_mappings),
    insurance_data: sanitizeInsuranceDataFromApi(p.insurance_data)
  };
}

function diffPatientUpdatePayload(initialPayload, currentPayload) {
  const initialNorm = normalizePatientUpdatePayloadForDiff(initialPayload || {});
  const currentNorm = normalizePatientUpdatePayloadForDiff(currentPayload || {});
  const changed = {};
  Object.keys(currentPayload || {}).forEach((key) => {
    if (key === 'documents' || key === 'profilePicture') return; // handled in multipart branch only
    const left = initialNorm[key];
    const right = currentNorm[key];
    if (JSON.stringify(left) !== JSON.stringify(right)) {
      changed[key] = currentPayload[key];
    }
  });
  return changed;
}

const AddPatient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { practiceId } = useParams();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('editId');
  const isEditMode = !!editId;
  const dispatch = useDispatch();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const { systemCaregivers = [], practiceProviders = [], practiceCaregivers = [], currentPractice } = useSelector(state => state.practice ?? {});

  // Safe Data Handling
  const safeSystemCaregivers = Array.isArray(systemCaregivers) ? systemCaregivers : (systemCaregivers?.caregivers || systemCaregivers?.data || []);
  const safePracticeProviders = Array.isArray(practiceProviders) ? practiceProviders : (practiceProviders?.data || []);
  const safePracticeCaregivers = Array.isArray(practiceCaregivers) ? practiceCaregivers : (practiceCaregivers?.data || []);

  const isActiveEntity = (row) => {
    const s = row?.status;
    if (s === 1 || s === true) return true;
    if (typeof s === 'string') return s.toLowerCase() === 'active';
    return false;
  };
  const activeSystemCaregivers = useMemo(
    () => safeSystemCaregivers.filter(isActiveEntity),
    [safeSystemCaregivers]
  );
  const activePracticeProviders = useMemo(
    () => safePracticeProviders.filter(isActiveEntity),
    [safePracticeProviders]
  );
  const activePracticeCaregivers = useMemo(
    () => safePracticeCaregivers.filter(isActiveEntity),
    [safePracticeCaregivers]
  );

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { sendPatientAdditionNotifications } = useEmailNotifications();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentLogoFailed, setConsentLogoFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Initial state is true in Edit mode to avoid flicker
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(!!editId);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  /** Pending delete: native `confirm()` shows "localhost says" — use in-app modals instead. */
  const [deleteMeterConfirm, setDeleteMeterConfirm] = useState(null);
  const [deleteDocumentConfirm, setDeleteDocumentConfirm] = useState(null);

  const [formData, setFormData] = useState(() => emptyAddPatientFormState());

  const systemCaregiverPickerOptions = activeSystemCaregivers;
  const practiceCaregiverPickerOptions = activePracticeCaregivers;

  // Keep selections restricted to active entities only (add + edit modes).
  useEffect(() => {
    const activeProviderIds = new Set(activePracticeProviders.map((p) => String(p.id)));
    const activePracticeCgIds = new Set(activePracticeCaregivers.map((c) => String(c.id)));
    const activeSystemCgIds = new Set(activeSystemCaregivers.map((c) => String(c.id)));

    setFormData((prev) => {
      const nextProvider = prev.provider && activeProviderIds.has(String(prev.provider)) ? prev.provider : '';
      const nextPracticeCg = (prev.practiceCaregiverIds || []).filter((id) => activePracticeCgIds.has(String(id)));
      const nextSystemCg = (prev.systemCaregiverIds || []).filter((id) => activeSystemCgIds.has(String(id)));

      const providerChanged = nextProvider !== prev.provider;
      const practiceCgChanged = nextPracticeCg.length !== (prev.practiceCaregiverIds || []).length;
      const systemCgChanged = nextSystemCg.length !== (prev.systemCaregiverIds || []).length;
      if (!providerChanged && !practiceCgChanged && !systemCgChanged) return prev;

      return {
        ...prev,
        provider: nextProvider,
        practiceCaregiverIds: nextPracticeCg,
        systemCaregiverIds: nextSystemCg
      };
    });
  }, [activePracticeProviders, activePracticeCaregivers, activeSystemCaregivers]);


  /** Coerce API / FormData caregiver id lists (arrays, JSON strings, nested "[1,2]" inside arrays) to unique string ids. */
  const normalizeMultiIds = (raw) => {
    const parts = [];
    const visit = (v) => {
      if (v == null || v === '') return;
      if (Array.isArray(v)) {
        v.forEach(visit);
        return;
      }
      if (typeof v === 'object' && v !== null) {
        if (v.value != null) {
          visit(v.value);
          return;
        }
        if (v.id != null) {
          visit(v.id);
          return;
        }
      }
      const s = String(v).trim();
      if (!s) return;
      if (s.startsWith('[')) {
        try {
          const inner = JSON.parse(s);
          if (Array.isArray(inner)) {
            inner.forEach(visit);
            return;
          }
        } catch {
          /* fall through */
        }
      }
      if (s.includes(',')) {
        s.split(',').forEach((p) => visit(p.trim()));
        return;
      }
      const cleaned = s.replace(/^\[|\]$/g, '').trim();
      if (cleaned) parts.push(cleaned);
    };
    visit(raw);
    return [...new Set(parts.filter(Boolean))];
  };

  const [errors, setErrors] = useState({});
  const [monitoringTypes, setMonitoringTypes] = useState({ bloodPressure: false, bloodGlucose: false, weight: false });
  const [monitoringValues, setMonitoringValues] = useState({ bloodPressure: 'I50.1', bloodGlucose: 'R73.0', weight: 'ICD-10' });
  const [practiceMonitoringTypes, setPracticeMonitoringTypes] = useState([]); // Store practice's configured monitoring types
  const [meterMappings, setMeterMappings] = useState([emptyMeterMappingRow()]);
  const [products, setProducts] = useState([]); // Products for model number dropdown
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [insuranceData, setInsuranceData] = useState({
    primary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' },
    secondary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' }
  });
  const [documents, setDocuments] = useState([{ fileName: '', file: null }]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [accountNumber, setAccountNumber] = useState(null);

  const [patientMetersList, setPatientMetersList] = useState([]);
  const [patientDocumentsList, setPatientDocumentsList] = useState([]);
  const [editingMeterId, setEditingMeterId] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [profileFileName, setProfileFileName] = useState('');
  const fileInputRef = React.useRef(null);

  const [consentForm, setConsentForm] = useState({
    name: '', dob: '', tel: '', address: '', email: '',
    patientSignature: '', patientDate: new Date().toISOString().split('T')[0],
    clinicianSignature: '', clinicianDate: new Date().toISOString().split('T')[0],
    caregiverName: '', caregiverSignature: '', caregiverDate: ''
  });

  const [initialData, setInitialData] = useState(null);

  // Document row errors (size + file name) stay in sync as soon as the user
  // picks files or edits names — no need to wait for Add / Save.
  useEffect(() => {
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (/^documentFile(Name)?_\d+$/.test(k)) delete next[k];
      });
      Object.assign(next, buildDocumentFieldErrors(documents));
      return next;
    });
  }, [documents]);

  const isChanged = useMemo(() => {
    if (!isEditMode || !initialData) return true;

    const hasNewProfilePicture = formData.profilePicture instanceof File;

    const currentData = {
      formData: { ...formData, profilePicture: null },
      hasNewProfilePicture,
      monitoringTypes,
      monitoringValues,
      insuranceData,
      termsAccepted,
      meterMappings: meterMappings.filter(m => isCompleteMeterFormRow(m)),
      documents: documents.map((d) => ({
        fileName: String(d?.fileName || '').trim(),
        hasFile: !!d?.file,
        fileTooLarge: Boolean(d?.fileTooLargeName)
      }))
    };

    // Simple deep comparison using JSON.stringify for these plain objects/arrays
    return JSON.stringify(currentData) !== JSON.stringify(initialData);
  }, [isEditMode, initialData, formData, monitoringTypes, monitoringValues, insuranceData, termsAccepted, meterMappings, documents]);
  const hasPasswordReuseError = String(errors.password || '').toLowerCase().includes('different from current password');

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} - ${phoneNumber.slice(6, 10)}`;
  };

  useEffect(() => {
    // Fetch system caregivers filtered by practiceId
    if (practiceId) {
      dispatch(fetchSystemCaregivers(practiceId));
      dispatch(fetchPracticeProviders({ practiceId, force: true }));
      dispatch(fetchPracticeCaregivers({ practiceId, force: true }));
      dispatch(fetchPractice(practiceId)); // Fetch practice details to get monitoring types
    }

    // Full inventory list for meter mapping (same catalog as Inventory List)
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await inventoryService.getProducts({
          page: 1,
          limit: 9999,
          search: ''
        });
        if (response.success && Array.isArray(response.data)) {
          setProducts(response.data);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Failed to load products for meter mapping');
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();

    // Fetch next account number (only in create mode)
    if (!isEditMode && practiceId) {
      const fetchAccountNumber = async () => {
        try {
          const response = await patientService.getNextAccountNumber(practiceId);
          if (response.data?.success && response.data?.data) {
            setAccountNumber(response.data.data.accountNumber);
          }
        } catch (error) {
          console.error('Error fetching account number:', error);
        }
      };
      fetchAccountNumber();
    }
  }, [dispatch, practiceId, isEditMode]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    if (!practiceId) return;
    const practiceName = currentPractice?.practice_name || currentPractice?.name || 'Practice';
    if (isEditMode) {
      // From patient list: Practice → [Practice Name] → Patients → Edit Patient
      updateBreadcrumbs([
        { label: 'Practices', path: '/practices' },
        { label: practiceName, path: `/practices/${practiceId}?tab=practice` },
        { label: 'Patients', path: `/practices/${practiceId}?tab=patient` },
        { label: 'Edit Patient', path: null, active: true }
      ]);
    } else {
      // Practice → [Practice Name] → Patients → Add Patient
      updateBreadcrumbs([
        { label: 'Practices', path: '/practices' },
        { label: practiceName, path: `/practices/${practiceId}?tab=practice` },
        { label: 'Patients', path: `/practices/${practiceId}?tab=patient` },
        { label: 'Add Patient', path: null, active: true }
      ]);
    }
  }, [practiceId, isEditMode, currentPractice, updateBreadcrumbs]);

  // Practice monitoring catalog (normalized). Do not reset checkbox state here — currentPractice refetches
  // would clear the user's selections and falsely fail validateForm on submit.
  useEffect(() => {
    if (!currentPractice?.selected_monitoring_types) {
      setPracticeMonitoringTypes([]);
      return;
    }
    setPracticeMonitoringTypes(normalizePracticeMonitoringTypes(currentPractice.selected_monitoring_types));
  }, [currentPractice?.id, currentPractice?.selected_monitoring_types]);

  // Create mode: when switching practice, reset monitoring toggles once (not on every practice poll).
  useEffect(() => {
    if (isEditMode) return;
    setMonitoringTypes({ bloodPressure: false, bloodGlucose: false, weight: false });
    setMonitoringValues({ bloodPressure: 'I50.1', bloodGlucose: 'R73.0', weight: 'ICD-10' });
  }, [practiceId, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !editId || !practiceId) return;
    let cancelled = false;

    setFormData(emptyAddPatientFormState());
    setPatientDocumentsList([]);
    setPatientMetersList([]);
    setInsuranceData({
      primary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' },
      secondary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' }
    });
    setMeterMappings([emptyMeterMappingRow()]);
    setDocuments([{ fileName: '', file: null }]);
    setMonitoringTypes({ bloodPressure: false, bloodGlucose: false, weight: false });
    setMonitoringValues({ bloodPressure: 'I50.1', bloodGlucose: 'R73.0', weight: 'ICD-10' });
    setTermsAccepted(false);
    setProfilePreview(null);
    setConsentForm({
      name: '', dob: '', tel: '', address: '', email: '',
      patientSignature: '', patientDate: new Date().toISOString().split('T')[0],
      clinicianSignature: '', clinicianDate: new Date().toISOString().split('T')[0],
      caregiverName: '', caregiverSignature: '', caregiverDate: ''
    });
    setAccountNumber(null);
    setErrors({});
    setEditingMeterId(null);

    const loadEditPatient = async () => {
      setIsLoadingPatientData(true);
      try {
        const pRes = await patientService.getPatient(practiceId, editId);
        if (cancelled) return;

        if (!pRes.data?.success) {
          toast.error(pRes.data?.message || 'Patient not found');
          return;
        }

        const patient = pRes.data.data;
        dispatch(setCurrentPatient(patient));

        const [metersSettled, docsSettled] = await Promise.allSettled([
          patientService.getPatientMeters(practiceId, editId),
          patientService.getPatientDocuments(practiceId, editId)
        ]);
        if (cancelled) return;

        const statusNumMap = { 2: 'Active', 3: 'Pending', 4: 'Locked' };
        const rawStatus = patient.status;
        const statusValue = typeof rawStatus === 'number' ? (statusNumMap[rawStatus] || 'Active')
          : (typeof rawStatus === 'string' && ['Active', 'Pending', 'Locked'].includes(rawStatus) ? rawStatus : 'Active');

        let genderValue = patient.gender || 'Male';
        if (patient.gender === 1 || patient.gender === '1') genderValue = 'Male';
        else if (patient.gender === 2 || patient.gender === '2') genderValue = 'Female';
        else if (patient.gender === 3 || patient.gender === '3') genderValue = 'Other';

        const stateIdRaw = patient.address?.state_id ?? patient.state_id;
        const parsedStateId = Number.parseInt(String(stateIdRaw ?? ''), 10);
        const stateObj = Number.isNaN(parsedStateId) ? null : US_STATES.find(s => s.id === parsedStateId);
        const stateName =
          patient.state ||
          patient.address?.state ||
          (stateObj ? stateObj.name : '');
        const dobRaw =
          patient.date_of_birth ??
          patient.dob ??
          patient.dateOfBirth ??
          patient.birth_date ??
          patient.birthDate ??
          patient.rpm_start_date ??
          patient.rpmStartDate ??
          patient.user?.date_of_birth ??
          patient.user?.dob;
        const dobValue = (() => {
          if (!dobRaw) return '';
          const v = String(dobRaw).trim();
          if (!v || v === '0000-00-00' || v === '0000-00-00 00:00:00') return '';

          // ISO-like values
          if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);

          // MM/DD/YYYY or M/D/YYYY
          const slash = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (slash) {
            const mm = slash[1].padStart(2, '0');
            const dd = slash[2].padStart(2, '0');
            return `${slash[3]}-${mm}-${dd}`;
          }

          // Fallback: Date parser for other formats
          const d = new Date(v);
          if (!Number.isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
          }
          return '';
        })();

        const rawHome = patient.homePhone || patient.phone || patient.phone_number || '';
        const rawCell = patient.cellPhone || patient.cell_phone || patient.cell_phone_number || '';
        const homeDigits = rawHome ? String(rawHome).replace(/\D/g, '') : '';
        const cellDigits = rawCell ? String(rawCell).replace(/\D/g, '') : '';

        const titleVal = (() => {
          const tid = patient.title_id ?? patient.title;
          if (tid == null || tid === '') return '';
          const n = Number(tid);
          if (Number.isFinite(n) && n > 0) return String(n);
          return String(tid);
        })();

        setFormData({
          title: titleVal,
          firstName: patient.first_name || '',
          middleInitial: (patient.middle_name != null && patient.middle_name !== '') ? String(patient.middle_name).trim() : '',
          lastName: patient.last_name || '',
          username: patient.username || '',
          password: '',
          confirmPassword: '',
          addressLine1: patient.address_line_1 || patient.address?.address_line_1 || '',
          addressLine2: patient.address_line_2 || patient.address?.address_line_2 || '',
          city: sanitizeCity(patient.city || patient.address?.city || ''),
          state: stateName,
          stateId: Number.isNaN(parsedStateId) ? null : parsedStateId,
          zipCode: (patient.zip_code != null && patient.zip_code !== '') || (patient.address?.zip_code != null && patient.address?.zip_code !== '')
            ? String(patient.zip_code ?? patient.address?.zip_code ?? '')
            : '',
          homePhone: homeDigits ? formatPhoneNumber(homeDigits) : '',
          cellPhone: cellDigits ? formatPhoneNumber(cellDigits) : '',
          fax: patient.fax ? formatPhoneNumber(patient.fax) : '',
          email: patient.email || '',
          gender: genderValue,
          dateOfBirth: dobValue,
          twoWayAuth: patient.is_two_way_auth === 1 || patient.is_two_way_auth === '1' ? 'Enabled' : 'Disabled',
          status: statusValue,
          timeZone: normalizeTimeZone(patient.time_zone ?? patient.timeZone),
          rpmStartDate: patient.rpm_start_date ? String(patient.rpm_start_date).split('T')[0] : '',
          provider: patient.provider_id ? String(patient.provider_id) : '',
          systemCaregiverIds: (() => {
            const list = normalizeMultiIds(patient.system_caregiver_ids ?? patient.systemCaregiverIds);
            if (list.length > 0) return list;
            const one = patient.system_caregiver_id ?? patient.system_caregiver?.id ?? patient.systemCaregiverId;
            return one != null && String(one).trim() !== '' ? [String(one)] : [];
          })(),
          practiceCaregiverIds: (() => {
            const fromJunction = normalizeMultiIds(patient.practice_caregiver_ids ?? patient.practiceCaregiverIds);
            if (fromJunction.length > 0) return fromJunction;
            const fromColumn = normalizeMultiIds(patient.caregiver_id);
            if (fromColumn.length > 0) return fromColumn;
            const one = patient.practice_caregiver_id ?? patient.practiceCaregiver?.id;
            return one != null && String(one).trim() !== '' ? [String(one)] : [];
          })(),
          profilePicture: null
        });

        const explicitTermsFalse =
          patient.agreement === 0 ||
          patient.agreement === '0' ||
          patient.agreement === false ||
          patient.termsAndConditions === false ||
          patient.termsAndConditions === 0 ||
          patient.termsAndConditions === '0' ||
          patient.terms_and_conditions === false ||
          patient.terms_and_conditions === 0 ||
          patient.terms_and_conditions === '0';
        const explicitTermsTrue =
          patient.agreement === 1 || patient.agreement === '1' || patient.agreement === true ||
          patient.termsAndConditions === true || patient.termsAndConditions === 1 || patient.termsAndConditions === '1' ||
          patient.terms_and_conditions === true || patient.terms_and_conditions === 1 || patient.terms_and_conditions === '1';
        // Per UX request: edit mode should come up checked by default.
        setTermsAccepted(isEditMode ? true : explicitTermsTrue);

        const insurance = patient.insurance_details || patient.insurance_data || patient.insuranceDetails;
        if (insurance) {
          setInsuranceData(sanitizeInsuranceDataFromApi(insurance));
        }

        const hasAnyMonitoringEnabled = (obj) => {
          if (!obj || typeof obj !== 'object') return false;
          const toBool = (v) => v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
          return (
            toBool(obj.bloodPressure) || toBool(obj.bp) || toBool(obj.BP) || toBool(obj.blood_pressure) ||
            toBool(obj.bloodGlucose) || toBool(obj.bg) || toBool(obj.BG) || toBool(obj.blood_glucose) ||
            toBool(obj.weight) || toBool(obj.wt) || toBool(obj.WT)
          );
        };
        const monitoringSource =
          (patient.monitoring_types && (Array.isArray(patient.monitoring_types) || hasAnyMonitoringEnabled(patient.monitoring_types)))
            ? patient.monitoring_types
            : (patient.monitoringTypes && (Array.isArray(patient.monitoringTypes) || hasAnyMonitoringEnabled(patient.monitoringTypes)))
              ? patient.monitoringTypes
              : patient.vitals;
        const mTypes = monitoringSource;
        const normalizeMonitoringTypeToken = (rawType) => {
          if (rawType === null || rawType === undefined) return null;
          const v = String(rawType).trim().toLowerCase();
          if (v === '1' || v === 'bp' || v === 'bloodpressure' || v === 'blood pressure' || v === 'blood_pressure') return 'bloodPressure';
          if (v === '2' || v === 'bg' || v === 'bloodglucose' || v === 'blood glucose' || v === 'blood_glucose') return 'bloodGlucose';
          if (v === '3' || v === 'wt' || v === 'weight') return 'weight';
          return null;
        };
        if (Array.isArray(mTypes)) {
          const newTypes = { bloodPressure: false, bloodGlucose: false, weight: false };
          const newValues = { bloodPressure: 'I50.1', bloodGlucose: 'R73.0', weight: 'ICD-10' };
          mTypes.forEach(m => {
            const normalized = normalizeMonitoringTypeToken(m?.type ?? m?.measured_type_id ?? m?.measuredTypeId ?? m);
            if (normalized === 'bloodPressure') {
              newTypes.bloodPressure = true;
              newValues.bloodPressure = m.icdCode || m.icd_code || 'I50.1';
            }
            if (normalized === 'bloodGlucose') {
              newTypes.bloodGlucose = true;
              newValues.bloodGlucose = m.icdCode || m.icd_code || 'R73.0';
            }
            if (normalized === 'weight') {
              newTypes.weight = true;
              newValues.weight = m.icdCode || m.icd_code || 'ICD-10';
            }
          });
          setMonitoringTypes(newTypes);
          setMonitoringValues(newValues);
        } else if (typeof mTypes === 'string' && mTypes.trim() !== '') {
          const newTypes = { bloodPressure: false, bloodGlucose: false, weight: false };
          mTypes
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean)
            .forEach((token) => {
              const normalized = normalizeMonitoringTypeToken(token);
              if (normalized) newTypes[normalized] = true;
            });
          setMonitoringTypes(newTypes);
        } else if (mTypes && typeof mTypes === 'object') {
          setMonitoringTypes({
            bloodPressure: !!(mTypes.bloodPressure ?? mTypes.bp ?? mTypes.BP ?? mTypes.blood_pressure),
            bloodGlucose: !!(mTypes.bloodGlucose ?? mTypes.bg ?? mTypes.BG ?? mTypes.blood_glucose),
            weight: !!(mTypes.weight ?? mTypes.wt ?? mTypes.WT)
          });
          const mValues = patient.monitoring_values || patient.monitoringValues || {};
          setMonitoringValues({
            bloodPressure: mValues.bloodPressure || 'I50.1',
            bloodGlucose: mValues.bloodGlucose || 'R73.0',
            weight: mValues.weight || 'ICD-10'
          });
        }

        if (patient.profile_pic) {
          const picUrl = buildPatientProfileImageUrl(patient.profile_pic);
          setProfilePreview(picUrl);
          setProfileFileName(String(patient.profile_pic).split(/[/\\]/).pop() || String(patient.profile_pic));
        } else {
          setProfilePreview(null);
          setProfileFileName('');
        }

        const fromApi = parsePatientConsentFromPayload(patient);
        const fromStorage = readPatientConsentDraft(practiceId, editId);
        setConsentForm(buildEditModeConsentFormState({
          patient,
          dobValue,
          homeDigits,
          formatPhoneNumberFn: formatPhoneNumber,
          fromApi,
          fromStorage
        }));

        setAccountNumber(patient.id ? String(patient.id) : (patient.patient_id || null));

        let meters = [];
        if (metersSettled.status === 'fulfilled') {
          const mr = metersSettled.value;
          if (mr?.data?.success && Array.isArray(mr.data.data)) meters = mr.data.data;
        } else {
          console.error('getPatientMeters failed:', metersSettled.reason);
        }
        if (meters.length === 0) {
          const raw = patient.meterMappings || patient.meter_mappings;
          if (Array.isArray(raw)) {
            const hasMeterPayload = (m) => {
              if (!m) return false;
              if (m.id != null && m.id !== '') return true;
              return !!(
                m.modelNumber || m.serialNumber || m.model_number || m.serial_number ||
                m.extension || m.gatewaySerialNumber || m.gateway_serial_number
              );
            };
            meters = raw
              .filter(hasMeterPayload)
              .map((m) => ({
                id: m.id,
                model_number: m.model_number ?? m.modelNumber ?? '',
                extension: m.extension ?? '',
                serial_number: m.serial_number ?? m.serialNumber ?? '',
                gateway_serial_number: m.gateway_serial_number ?? m.gatewaySerialNumber ?? '',
                product_name: m.product_name,
                created_at: m.created_at
              }));
          }
        }
        setPatientMetersList(meters);
        // Edit mode: saved devices only in "Mapped devices" table — meter mapping rows are for new devices only
        setMeterMappings([emptyMeterMappingRow()]);

        let docRows = [];
        if (docsSettled.status === 'fulfilled') {
          docRows = extractDocumentsRowsFromResponse(docsSettled.value);
        } else {
          console.error('getPatientDocuments failed:', docsSettled.reason);
        }
        if (docRows.length === 0) {
          const raw = patient.patient_documents || patient.documents;
          if (Array.isArray(raw)) {
            docRows = raw;
          }
        }
        setPatientDocumentsList(docRows.map(d => ({
          id: d.id,
          document_name: d.name ?? d.document_name,
          file_name: d.document ?? d.file_name,
          created_at: d.created_at,
          ...d
        })));

        const initialMonitoringTypes = (Array.isArray(mTypes)) ? (() => {
          const arr = [];
          mTypes.forEach((m) => {
            const normalized = normalizeMonitoringTypeToken(m?.type ?? m?.measured_type_id ?? m?.measuredTypeId ?? m);
            if (normalized === 'bloodPressure') arr.push({ type: 'bloodPressure', icdCode: m?.icdCode || m?.icd_code || 'I50.1' });
            if (normalized === 'bloodGlucose') arr.push({ type: 'bloodGlucose', icdCode: m?.icdCode || m?.icd_code || 'R73.0' });
            if (normalized === 'weight') arr.push({ type: 'weight', icdCode: m?.icdCode || m?.icd_code || 'ICD-10' });
          });
          return arr;
        })() : (() => {
          const vals = patient.monitoring_values || patient.monitoringValues || {};
          const arr = [];
          if (mTypes?.bloodPressure) arr.push({ type: 'bloodPressure', icdCode: vals.bloodPressure || 'I50.1' });
          if (mTypes?.bloodGlucose) arr.push({ type: 'bloodGlucose', icdCode: vals.bloodGlucose || 'R73.0' });
          if (mTypes?.weight) arr.push({ type: 'weight', icdCode: vals.weight || 'ICD-10' });
          return arr;
        })();

        const initialPracticeCgIds = (() => {
          const fromJunction = normalizeMultiIds(patient.practice_caregiver_ids ?? patient.practiceCaregiverIds);
          if (fromJunction.length > 0) return fromJunction.map((id) => parseInt(id, 10)).filter((n) => Number.isFinite(n) && n > 0);
          const fromColumn = normalizeMultiIds(patient.caregiver_id);
          if (fromColumn.length > 0) return fromColumn.map((id) => parseInt(id, 10)).filter((n) => Number.isFinite(n) && n > 0);
          const one = patient.practice_caregiver_id ?? patient.practiceCaregiver?.id;
          return one != null && String(one).trim() !== '' ? [parseInt(String(one), 10)].filter((n) => Number.isFinite(n) && n > 0) : [];
        })();

        const initialSystemCgIds = (() => {
          const list = normalizeMultiIds(patient.system_caregiver_ids ?? patient.systemCaregiverIds);
          if (list.length > 0) return list.map((id) => parseInt(id, 10)).filter((n) => Number.isFinite(n) && n > 0);
          const one = patient.system_caregiver_id ?? patient.system_caregiver?.id ?? patient.systemCaregiverId;
          return one != null && String(one).trim() !== '' ? [parseInt(String(one), 10)].filter((n) => Number.isFinite(n) && n > 0) : [];
        })();

        const initialApiPayload = normalizePatientUpdatePayloadForDiff({
          title: titleVal,
          firstName: patient.first_name || '',
          middleInitial: (patient.middle_name != null && patient.middle_name !== '') ? String(patient.middle_name).trim() : '',
          lastName: patient.last_name || '',
          username: patient.username || '',
          email: patient.email || '',
          gender: genderValue,
          dateOfBirth: dobValue,
          homePhone: homeDigits ? formatPhoneNumber(homeDigits) : '',
          cellPhone: cellDigits ? formatPhoneNumber(cellDigits) : '',
          fax: patient.fax ? formatPhoneNumber(patient.fax) : '',
          addressLine1: patient.address_line_1 || patient.address?.address_line_1 || '',
          addressLine2: patient.address_line_2 || patient.address?.address_line_2 || '',
          city: sanitizeCity(patient.city || patient.address?.city || ''),
          state: stateName,
          state_id: Number.isNaN(parsedStateId) ? null : parsedStateId,
          zipCode: (patient.zip_code != null && patient.zip_code !== '') || (patient.address?.zip_code != null && patient.address?.zip_code !== '')
            ? String(patient.zip_code ?? patient.address?.zip_code ?? '')
            : '',
          status: statusValue,
          timeZone: normalizeTimeZone(patient.time_zone ?? patient.timeZone),
          rpmStartDate: patient.rpm_start_date ? String(patient.rpm_start_date).split('T')[0] : '',
          provider: patient.provider_id ? String(patient.provider_id) : '',
          is_two_way_auth: patient.is_two_way_auth === 1 || patient.is_two_way_auth === '1' ? 1 : 0,
          practice_caregiver_ids: initialPracticeCgIds,
          system_caregiver_ids: initialSystemCgIds,
          monitoring_types: initialMonitoringTypes,
          meter_mappings: dedupeMeterSubmitRows(meters.map(patientListMeterToSubmitRow).filter(isCompleteSubmitRow)),
          insurance_data: insurance ? sanitizeInsuranceDataFromApi(insurance) : {
            primary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' },
            secondary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' }
          }
        });

        // Capture initial state for change detection
        setInitialData({
          patientUserId: patient.user_id ?? patient.userId ?? null,
          hasNewProfilePicture: false,
          formData: {
            title: titleVal,
            firstName: patient.first_name || '',
            middleInitial: (patient.middle_name != null && patient.middle_name !== '') ? String(patient.middle_name).trim() : '',
            lastName: patient.last_name || '',
            username: patient.username || '',
            password: '',
            confirmPassword: '',
            addressLine1: patient.address_line_1 || patient.address?.address_line_1 || '',
            addressLine2: patient.address_line_2 || patient.address?.address_line_2 || '',
            city: sanitizeCity(patient.city || patient.address?.city || ''),
            state: stateName,
            stateId: Number.isNaN(parsedStateId) ? null : parsedStateId,
            zipCode: (patient.zip_code != null && patient.zip_code !== '') || (patient.address?.zip_code != null && patient.address?.zip_code !== '')
              ? String(patient.zip_code ?? patient.address?.zip_code ?? '')
              : '',
            homePhone: homeDigits ? formatPhoneNumber(homeDigits) : '',
            cellPhone: cellDigits ? formatPhoneNumber(cellDigits) : '',
            fax: patient.fax ? formatPhoneNumber(patient.fax) : '',
            email: patient.email || '',
            gender: genderValue,
            dateOfBirth: dobValue,
            twoWayAuth: patient.is_two_way_auth === 1 || patient.is_two_way_auth === '1' ? 'Enabled' : 'Disabled',
            status: statusValue,
            timeZone: normalizeTimeZone(patient.time_zone ?? patient.timeZone),
            rpmStartDate: patient.rpm_start_date ? String(patient.rpm_start_date).split('T')[0] : '',
            provider: patient.provider_id ? String(patient.provider_id) : '',
            systemCaregiverIds: (() => {
              const list = normalizeMultiIds(patient.system_caregiver_ids ?? patient.systemCaregiverIds);
              if (list.length > 0) return list;
              const one = patient.system_caregiver_id ?? patient.system_caregiver?.id ?? patient.systemCaregiverId;
              return one != null && String(one).trim() !== '' ? [String(one)] : [];
            })(),
            practiceCaregiverIds: (() => {
              const fromJunction = normalizeMultiIds(patient.practice_caregiver_ids ?? patient.practiceCaregiverIds);
              if (fromJunction.length > 0) return fromJunction;
              const fromColumn = normalizeMultiIds(patient.caregiver_id);
              if (fromColumn.length > 0) return fromColumn;
              const one = patient.practice_caregiver_id ?? patient.practiceCaregiver?.id;
              return one != null && String(one).trim() !== '' ? [String(one)] : [];
            })(),
            profilePicture: null
          },
          monitoringTypes: (Array.isArray(mTypes)) ? (() => {
            const newTypes = { bloodPressure: false, bloodGlucose: false, weight: false };
            mTypes.forEach(m => {
              const normalized = normalizeMonitoringTypeToken(m?.type ?? m?.measured_type_id ?? m?.measuredTypeId ?? m);
              if (normalized === 'bloodPressure') newTypes.bloodPressure = true;
              if (normalized === 'bloodGlucose') newTypes.bloodGlucose = true;
              if (normalized === 'weight') newTypes.weight = true;
            });
            return newTypes;
          })() : (mTypes && typeof mTypes === 'object' ? {
            bloodPressure: !!mTypes.bloodPressure,
            bloodGlucose: !!mTypes.bloodGlucose,
            weight: !!mTypes.weight
          } : { bloodPressure: false, bloodGlucose: false, weight: false }),
          monitoringValues: (Array.isArray(mTypes)) ? (() => {
            const newValues = { bloodPressure: 'I50.1', bloodGlucose: 'R73.0', weight: 'ICD-10' };
            mTypes.forEach(m => {
              const normalized = normalizeMonitoringTypeToken(m?.type ?? m?.measured_type_id ?? m?.measuredTypeId ?? m);
              if (normalized === 'bloodPressure') newValues.bloodPressure = m?.icdCode || m?.icd_code || 'I50.1';
              if (normalized === 'bloodGlucose') newValues.bloodGlucose = m?.icdCode || m?.icd_code || 'R73.0';
              if (normalized === 'weight') newValues.weight = m?.icdCode || m?.icd_code || 'ICD-10';
            });
            return newValues;
          })() : (mTypes && typeof mTypes === 'object' ? {
            bloodPressure: (patient.monitoring_values || patient.monitoringValues || {}).bloodPressure || 'I50.1',
            bloodGlucose: (patient.monitoring_values || patient.monitoringValues || {}).bloodGlucose || 'R73.0',
            weight: (patient.monitoring_values || patient.monitoringValues || {}).weight || 'ICD-10'
          } : { bloodPressure: 'I50.1', bloodGlucose: 'R73.0', weight: 'ICD-10' }),
          insuranceData: insurance ? sanitizeInsuranceDataFromApi(insurance) : {
            primary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' },
            secondary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' }
          },
          termsAccepted: isEditMode ? true : explicitTermsTrue,
          meterMappings: [], // No new meter mappings in initial load
          documents: documents.map((d) => ({
            fileName: String(d?.fileName || '').trim(),
            hasFile: !!d?.file
          })),
          apiPayload: initialApiPayload
        });

        console.groupCollapsed(`[AddPatient] EDIT — fetched (practiceId=${practiceId}, editId=${editId})`);
        console.log('getPatient → patient (full):', patient);
        console.log('Caregivers:', {
          practice_caregiver_ids: patient.practice_caregiver_ids,
          practiceCaregiverIds: patient.practiceCaregiverIds,
          practice_caregiver_id: patient.practice_caregiver_id,
          caregiver_id: patient.caregiver_id,
          normalizedPractice: normalizeMultiIds(patient.practice_caregiver_ids ?? patient.practiceCaregiverIds),
          system_caregiver_ids: patient.system_caregiver_ids,
          systemCaregiverIds: patient.systemCaregiverIds,
          normalizedSystem: normalizeMultiIds(patient.system_caregiver_ids ?? patient.systemCaregiverIds)
        });
        console.log('Title / middle:', { title_id: patient.title_id, middle_name: patient.middle_name });
        console.log('Insurance:', patient.insurance_details || patient.insurance_data || patient.insuranceDetails);
        console.log('Embedded meters (getPatient):', patient.meterMappings || patient.meter_mappings);
        console.log(
          'Note: API often includes one empty meter row as placeholder when DB has no meter_mapping rows; getPatientMeters count is authoritative for saved devices.'
        );
        console.log('Embedded docs (getPatient):', patient.patient_documents || patient.documents);
        console.log(
          'getPatientMeters:',
          metersSettled.status === 'fulfilled' ? metersSettled.value?.data : { status: 'rejected', reason: metersSettled.reason }
        );
        console.log(
          'getPatientDocuments:',
          docsSettled.status === 'fulfilled' ? docsSettled.value?.data : { status: 'rejected', reason: docsSettled.reason }
        );
        console.log('Mapped devices (table) count:', meters.length, '| meter mapping form: new rows only | document row count:', docRows.length);
        console.groupEnd();
      } finally {
        setIsLoadingPatientData(false);
      }
    };

    loadEditPatient();
    return () => { cancelled = true; };
    // location.key: refetch when user returns to this screen (e.g. edited in PatientEditView, then list → edit again)
  }, [dispatch, isEditMode, editId, practiceId, location.key]);

  const openDeleteMeterConfirm = (meter) => {
    const id = meter?.id;
    if (id == null) {
      toast.error('Cannot remove this mapping (missing id).');
      return;
    }
    if (!practiceId || !editId) return;
    setDeleteMeterConfirm(meter);
  };

  const handleDeleteMappedMeterConfirmed = async () => {
    const meter = deleteMeterConfirm;
    if (!meter || meter.id == null) return;
    const id = meter.id;
    const previousList = patientMetersList;
    setDeleteMeterConfirm(null);
    setPatientMetersList((prev) => prev.filter((m) => m.id !== id));
    if (editingMeterId === id) {
      setEditingMeterId(null);
      setMeterMappings([emptyMeterMappingRow()]);
    }
    toast.success('Meter mapping removed');
    try {
      const res = await patientService.deleteMeterFromPatient(practiceId, editId, id);
      if (!res.data?.success) {
        setPatientMetersList(previousList);
        toast.error(res.data?.message || 'Failed to remove meter');
      }
    } catch (err) {
      setPatientMetersList(previousList);
      toast.error(err.response?.data?.message || 'Failed to remove meter');
    }
  };

  const handleEditMappedMeter = (meter) => {
    if (meter?.id == null) return;
    setEditingMeterId(meter.id);
    const base = mapApiMeterToFormRow(meter);
    const pid = base.productId ?? products.find((p) => String(p.model_number) === String(base.modelNumber))?.id;
    const row = {
      ...base,
      ...(pid != null && pid !== '' ? { productId: Number(pid) } : {}),
      availableSerials: [],
      serialFetchLoading: false,
      serialSource: 'custom',
      serialHydratedFromList: true
    };
    setMeterMappings([row, emptyMeterMappingRow()]);
  };

  const handleCancelMeterEdit = () => {
    setEditingMeterId(null);
    setMeterMappings([emptyMeterMappingRow()]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    let newErrors = { ...errors };

    delete newErrors[name];

    // 1. Phone Number Masking & Validation
    if (['homePhone', 'cellPhone', 'fax'].includes(name)) {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        newValue = formatPhoneNumber(numericValue);
        if (name === 'homePhone' && numericValue.length === 0) {
          newErrors.homePhone = 'Home Phone is required';
        } else if (numericValue.length > 0 && numericValue.length < 10 && name !== 'fax') {
          newErrors[name] = 'Invalid phone format';
        } else {
          delete newErrors[name];
        }
        setErrors(newErrors);
      } else {
        return; // Prevent adding more than 10 digits
      }
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 2) {
          newErrors.zipCode = 'Zip code must be at least 2 characters';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // 1.2 Email Character Filtering (no spaces)
    if (name === 'email') {
      newValue = value.replace(/\s/g, '');
    }

    // 1.25 City: letters, spaces, hyphen, apostrophe only (no digits or other symbols)
    if (name === 'city') {
      newValue = value.replace(/[^a-zA-Z\s'-]/g, '');
    }

    // 1.3 Username Character Filtering
    if (name === 'username') {
      newValue = value.replace(/[^a-zA-Z0-9]/g, '');
    }

    // 2. Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return; 
      }
      if ((name === 'firstName' || name === 'lastName') && value.length > 20) {
        return;
      }
      if (name === 'middleInitial') {
        if (value.includes(' ')) {
          newErrors.middleInitial = 'Middle Initial cannot contain spaces';
        } else {
          delete newErrors.middleInitial;
        }
      }
    }

    // 3. Email Validation (real-time while typing and on blur)
    if (name === 'email') {
      const emailValue = (newValue || '').trim();
      if (!emailValue) {
        newErrors.email = 'Email is required';
      } else if (!isValidEmailFormat(emailValue)) {
        newErrors.email = 'Invalid email format';
      } else {
        delete newErrors.email;
      }
    }

    if (name === 'username') {
      if (!newValue) {
        delete newErrors.username;
      } else if (!/^[a-zA-Z0-9]+$/.test(newValue)) {
        newErrors.username = 'Username should only contain alphanumeric characters';
      } else {
        delete newErrors.username;
      }
    }

    // 3.1 State Sync
    if (name === 'state') {
      const stateObj = US_STATES.find(s => s.name === value);
      if (stateObj) {
        setFormData(prev => ({ ...prev, stateId: stateObj.id }));
        if (newErrors.state) delete newErrors.state;
      } else {
        setFormData(prev => ({ ...prev, stateId: null }));
      }
    }

    // 4. Password Validation (Real-time)
    if (name === 'password') {
      const passwordVal = String(newValue || '');
      if (!passwordVal) {
        delete newErrors.password;
        if (isEditMode) {
          // Edit mode: keep existing password when field is empty; no validation noise.
          delete newErrors.confirmPassword;
        }
      } else {
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          dob: formData.dateOfBirth
        };
        const validation = validatePassword(passwordVal, userData);
        if (!validation.isValid) {
          newErrors.password = validation.error;
        } else {
          delete newErrors.password;
        }
      }
    } else if (name === 'confirmPassword') {
      if (!newValue && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (isEditMode && !String(formData.password || '').trim()) {
        // In edit mode, ignore confirm field until user actually enters a new password.
        delete newErrors.confirmPassword;
      } else if (formData.password && newValue !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        delete newErrors.confirmPassword;
      }
    } else {
      // Real-time validation for required fields (username "required" is only on submit — validateForm)
      if (!newValue || !newValue.toString().trim()) {
        const fieldLabels = {
          firstName: 'First Name',
          lastName: 'Last Name',
          username: 'Username',
          addressLine1: 'Address',
          city: 'City',
          state: 'State',
          zipCode: 'Zip Code',
          provider: 'Provider',
          status: 'Status',
          timeZone: 'Time Zone',
          rpmStartDate: 'RPM Start Date',
          dateOfBirth: 'Date of Birth'
        };
        if (fieldLabels[name] && name !== 'username') {
          newErrors[name] = `${fieldLabels[name]} is required`;
        }
      } else {
        if (newErrors[name]) delete newErrors[name];
      }
    }

    if (name === 'dateOfBirth' && newValue) {
      const dob = new Date(newValue);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dob > today) {
        newErrors.dateOfBirth = 'Date of Birth cannot be in the future';
      } else {
        delete newErrors.dateOfBirth;
      }
    }

    setErrors(newErrors);
    setFormData(prev => ({ ...prev, [name]: newValue }));

    // Update Consent Form Data
    if (!termsAccepted && !isEditMode) {
      setConsentForm(prev => {
        const updates = {};
        if (name === 'firstName') updates.name = `${newValue} ${formData.lastName}`;
        if (name === 'lastName') updates.name = `${formData.firstName} ${newValue}`;
        if (name === 'dateOfBirth') updates.dob = newValue;
        if (name === 'homePhone') updates.tel = newValue;
        if (name === 'addressLine1') updates.address = newValue;
        if (name === 'email') updates.email = newValue;
        return { ...prev, ...updates };
      });
    }
  };

  const pickAscField = (name, idStr) => {
    handleInputChange({ target: { name, value: idStr } });
  };

  const addSystemCaregiverId = (idStr) => {
    setFormData((prev) => {
      if (prev.systemCaregiverIds.includes(idStr)) return prev;
      return { ...prev, systemCaregiverIds: [...prev.systemCaregiverIds, idStr] };
    });
    setErrors((prev) => {
      if (!prev.systemCaregiver) return prev;
      const next = { ...prev };
      delete next.systemCaregiver;
      return next;
    });
  };

  const removeSystemCaregiverId = (idStr) => {
    setFormData((prev) => {
      const nextIds = prev.systemCaregiverIds.filter((id) => id !== idStr);
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...(nextIds.length === 0 ? { systemCaregiver: 'Select at least one system caregiver' } : {})
      }));
      return {
        ...prev,
        systemCaregiverIds: nextIds
      };
    });
  };

  const addPracticeCaregiverId = (idStr) => {
    setFormData((prev) => {
      if (prev.practiceCaregiverIds.includes(idStr)) return prev;
      return { ...prev, practiceCaregiverIds: [...prev.practiceCaregiverIds, idStr] };
    });
    setErrors((prev) => {
      if (!prev.practiceCaregiver) return prev;
      const next = { ...prev };
      delete next.practiceCaregiver;
      return next;
    });
  };

  const removePracticeCaregiverId = (idStr) => {
    setFormData((prev) => {
      const nextIds = prev.practiceCaregiverIds.filter((id) => id !== idStr);
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...(nextIds.length === 0 ? { practiceCaregiver: 'Select at least one practice caregiver' } : {})
      }));
      return {
        ...prev,
        practiceCaregiverIds: nextIds
      };
    });
  };

  const handleMonitoringTypeChange = (type) => {
    setMonitoringTypes((prev) => {
      const next = { ...prev, [type]: !prev[type] };
      const hasMonitoringType = next.bloodPressure || next.bloodGlucose || next.weight;
      setErrors((prevErrors) => {
        const out = { ...prevErrors };
        if (practiceMonitoringTypes.length > 0 && !hasMonitoringType) {
          out.monitoringTypes = 'Select at least one monitoring type';
        } else {
          delete out.monitoringTypes;
        }
        return out;
      });
      return next;
    });
  };
  const handleMonitoringValueChange = (type, value) => setMonitoringValues(prev => ({ ...prev, [type]: value }));
  const addMeterMapping = () => setMeterMappings((prev) => [...prev, emptyMeterMappingRow()]);
  const updateMeterMapping = (index, field, value) =>
    setMeterMappings((prev) => prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)));
  const patchMeterMapping = (index, partial) =>
    setMeterMappings((prev) => prev.map((m, i) => (i === index ? { ...m, ...partial } : m)));

  const clearMeterSerialError = (index) => {
    const k = `meterSerial_${index}`;
    setErrors((prev) => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const validateMeterSerialWithServer = async (index) => {
    const row = meterMappings[index];
    if (!practiceId || !row?.productId) return;
    const serial = String(row.serialNumber || '').trim();
    const serialRegex = /^[a-zA-Z0-9-]+$/;
    if (!serial || serial.length < 8 || serial.length > 16 || !serialRegex.test(serial)) return;

    const excludeUserId =
      isEditMode && initialData?.patientUserId != null ? initialData.patientUserId : undefined;
    try {
      const res = await patientService.checkMeterSerialAvailability(practiceId, {
        serial,
        ...(excludeUserId != null ? { exclude_user_id: excludeUserId } : {})
      });
      const ok = res?.data?.success && res?.data?.data?.available;
      if (!ok) {
        const msg = res?.data?.data?.message || 'This serial is already assigned to another patient.';
        setErrors((prev) => ({ ...prev, [`meterSerial_${index}`]: msg }));
      }
    } catch {
      /* optional server check on blur */
    }
  };

  const handleMeterProductChange = (index, productIdStr) => {
    if (!productIdStr) {
      setMeterMappings((prev) =>
        prev.map((m, i) =>
          i === index
            ? {
                ...emptyMeterMappingRow(),
                ...(m.id != null ? { id: m.id } : {})
              }
            : m
        )
      );
      return;
    }
    const pid = parseInt(productIdStr, 10);
    const productRow = products.find((p) => Number(p.id) === pid);
    patchMeterMapping(index, {
      productId: Number.isFinite(pid) ? pid : null,
      modelNumber: productRow?.model_number || '',
      serialNumber: '',
      availableSerials: [],
      serialFetchLoading: false,
      serialSource: 'custom',
      serialHydratedFromList: true
    });
  };
  const removeMeterMapping = (index) => setMeterMappings(prev => prev.filter((_, i) => i !== index));
  const insuranceFieldMessages = {
    insuranceName: 'Insurance name is required',
    subscriberId: 'Subscriber ID: letters and numbers only (A–Z, a–z, 0–9)',
    insuredName: 'Insured name: letters, spaces, hyphens, and apostrophes only',
    groupNo: 'Group number: letters and numbers only (A–Z, a–z, 0–9)'
  };

  const handleInsuranceChange = (type, field, rawValue) => {
    const raw = String(rawValue ?? '');
    let cleaned = raw;
    if (field === 'insuranceName') cleaned = raw.replace(/[^a-zA-Z\s]/g, '');
    else if (field === 'subscriberId') cleaned = raw.replace(/[^a-zA-Z0-9]/g, '');
    else if (field === 'insuredName') cleaned = raw.replace(/[^a-zA-Z\s'-]/g, '');
    else if (field === 'groupNo') cleaned = raw.replace(/[^a-zA-Z0-9]/g, '');

    setInsuranceData((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: cleaned }
    }));

    const errKey =
      field === 'insuranceName'
        ? `${type}InsuranceName`
        : field === 'subscriberId'
          ? `${type}SubscriberId`
          : field === 'insuredName'
            ? `${type}InsuredName`
            : field === 'groupNo'
              ? `${type}GroupNo`
              : null;
    if (!errKey) return;

    setErrors((prev) => {
      const next = { ...prev };
      // Insurance name: sanitize on change only; show rule on submit (validateForm), not on every keystroke.
      if (field === 'insuranceName') {
        delete next[errKey];
        return next;
      }
      const t = cleaned.trim();
      const patterns = {
        insuranceName: /^[a-zA-Z\s]+$/,
        subscriberId: /^[a-zA-Z0-9]+$/,
        insuredName: /^[a-zA-Z\s'-]+$/,
        groupNo: /^[a-zA-Z0-9]+$/
      };
      const re = patterns[field];
      if (!t) {
        delete next[errKey];
        return next;
      }
      // If we stripped characters but the result is valid, clear error (avoid false positives on paste / hidden chars).
      if (raw !== cleaned && raw.length > 0) {
        if (re && re.test(t)) {
          delete next[errKey];
        } else {
          next[errKey] = insuranceFieldMessages[field] || 'Invalid characters removed';
        }
        return next;
      }
      if (re && !re.test(t)) {
        next[errKey] = insuranceFieldMessages[field];
      } else {
        delete next[errKey];
      }
      return next;
    });
  };

  const addDocument = () => setDocuments((prev) => [...prev, { fileName: '', file: null }]);
  const updateDocument = (index, field, value) => {
    if (field === 'fileName') {
      const raw = String(value ?? '');
      const v = raw.replace(/[^a-zA-Z0-9 ]/g, '');
      setDocuments((prev) => {
        const updated = prev.map((d, i) => (i === index ? { ...d, fileName: v } : d));
        return updated;
      });
      return;
    }
    setDocuments((prev) => {
      const updated = prev.map((d, i) => (i === index ? { ...d, [field]: value } : d));
      return updated;
    });
  };
  const removeDocument = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };
  const handleFileUpload = (e, index) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedMimes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/jpeg',
      'image/png',
      'application/xml',
      'text/xml'
    ];

    // flushSync: commit document rows before this handler returns so a fast
    // click on "Add patient" cannot run validateForm against stale state and
    // skip oversize validation.
    flushSync(() => {
      setDocuments((prev) => {
        const next = [...prev];

        const existingWithFiles = next.filter((d) => d.file instanceof File).length;
        let remainingSlots = Math.max(0, 50 - existingWithFiles);

        let cursor = index;
        for (const file of files) {
          if (!allowedMimes.includes(file.type)) {
            toast.error(`Invalid document type: ${file.name}. Allowed: PDF, Word, TXT, JPG, PNG, XML.`);
            continue;
          }
          if (file.size >= MAX_DOCUMENT_FILE_BYTES) {
            while (cursor < next.length && next[cursor]?.file instanceof File) {
              cursor += 1;
            }
            const errIdx = cursor < next.length ? cursor : next.length;
            const prevRow = errIdx < next.length ? next[errIdx] : {};
            const slot = {
              ...prevRow,
              file: null,
              fileTooLargeName: file.name,
              fileName: (prevRow.fileName || '').trim() ? prevRow.fileName : ''
            };
            if (errIdx < next.length) next[errIdx] = slot;
            else next.push(slot);
            continue;
          }
          if (remainingSlots <= 0) {
            toast.warning('Maximum 50 documents are allowed per patient.');
            break;
          }
          while (cursor < next.length && next[cursor]?.file instanceof File) {
            cursor += 1;
          }
          const defaultName = String(file.name || '').replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9 ]/g, '').trim();
          const row = {
            fileName: defaultName || 'Document',
            file,
            fileTooLargeName: undefined
          };
          if (cursor < next.length) {
            const { fileTooLargeName: _omit, ...rest } = next[cursor] || {};
            next[cursor] = { ...rest, fileName: row.fileName, file: row.file };
          } else {
            next.push(row);
          }
          cursor += 1;
          remainingSlots -= 1;
        }

        if (next.length === 0 || next[next.length - 1].file instanceof File) {
          next.push({ fileName: '', file: null });
        }

        return next;
      });
    });

    e.target.value = '';
  };

  const handleProfilePictureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Instant Validation: Type
    const allowedMimes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedMimes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        profilePicture: 'Invalid image type. Only JPG, PNG, GIF, and WebP are allowed.'
      }));
      e.target.value = '';
      return;
    }

    // Instant Validation: Size (10 MB — same limit as server / documents)
    const maxSize = MAX_DOCUMENT_FILE_BYTES;
    if (file.size >= maxSize) {
      setErrors((prev) => ({
        ...prev,
        profilePicture: 'Profile picture must be under 10 MB.'
      }));
      e.target.value = '';
      return;
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next.profilePicture;
      return next;
    });
    setFormData(prev => ({ ...prev, profilePicture: file }));
    setProfileFileName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const triggerProfileUpload = (e) => {
    e.preventDefault();
    fileInputRef.current.click();
  };

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  const handleCancel = (e) => {
    if (e) e.preventDefault();
    navigate(-1);
  };

  const handleTermsClick = (e) => {
    e.preventDefault();
    // Always open consent form on click (edit users may review/update it).
    setConsentForm(prev => ({
      ...prev,
      dob: formData.dateOfBirth
    }));
    setConsentLogoFailed(false);
    setShowConsentModal(true);
  };

  // --- Consent date range helpers --------------------------------------
  // Allowed range for signature dates: practice creation date and onward.
  // Dates BEFORE practice creation are disabled; today and any later date are
  // allowed (no upper bound).
  const practiceCreatedAtStr = useMemo(() => {
    const raw = currentPractice?.created_at || currentPractice?.createdAt;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    // local-date YYYY-MM-DD (avoid UTC shift)
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, [currentPractice?.created_at, currentPractice?.createdAt]);

  const CONSENT_DATE_FIELDS = ['patientDate', 'clinicianDate', 'caregiverDate'];
  const CONSENT_DATE_LABELS = {
    patientDate: 'Patient signature date',
    clinicianDate: 'Clinician signature date',
    caregiverDate: 'Caregiver/relative signature date'
  };

  const isConsentDateBeforePracticeCreation = (value) => {
    if (!value || !practiceCreatedAtStr) return false;
    return value < practiceCreatedAtStr;
  };

  const handleConsentChange = (e) => {
    const { name, value } = e.target;

    if (CONSENT_DATE_FIELDS.includes(name) && value && isConsentDateBeforePracticeCreation(value)) {
      toast.warning(
        `${CONSENT_DATE_LABELS[name]} cannot be earlier than the practice creation date (${practiceCreatedAtStr}).`
      );
      return;
    }

    setConsentForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAgreeToConsent = () => {
    // Required signature dates: patient, clinician, and caregiver/relative (all compulsory).
    const requiredDateFields = ['patientDate', 'clinicianDate', 'caregiverDate'];
    for (const f of requiredDateFields) {
      if (!consentForm[f]) {
        toast.error(`${CONSENT_DATE_LABELS[f]} is required.`);
        return;
      }
      if (isConsentDateBeforePracticeCreation(consentForm[f])) {
        toast.error(
          `${CONSENT_DATE_LABELS[f]} cannot be earlier than the practice creation date (${practiceCreatedAtStr}).`
        );
        return;
      }
    }

    setTermsAccepted(true);
    setShowConsentModal(false);
    if (errors.terms) setErrors(prev => ({ ...prev, terms: '' }));
  };

  // Create mode: if required consent signature dates are cleared or invalid, uncheck Terms & Conditions.
  useEffect(() => {
    if (isEditMode || !termsAccepted) return;
    if (!consentForm.patientDate || !consentForm.clinicianDate || !consentForm.caregiverDate) {
      setTermsAccepted(false);
      return;
    }
    if (isConsentDateBeforePracticeCreation(consentForm.patientDate)) {
      setTermsAccepted(false);
      return;
    }
    if (isConsentDateBeforePracticeCreation(consentForm.clinicianDate)) {
      setTermsAccepted(false);
      return;
    }
    if (isConsentDateBeforePracticeCreation(consentForm.caregiverDate)) {
      setTermsAccepted(false);
    }
  }, [
    isEditMode,
    termsAccepted,
    consentForm.patientDate,
    consentForm.clinicianDate,
    consentForm.caregiverDate,
    practiceCreatedAtStr
  ]);

  const handleDownloadPDF = () => {
    const element = document.getElementById('printable-consent-form');
    const opt = {
      margin: [5, 10, 5, 10], // Adjusted margins
      filename: `Consent_Form_${consentForm.name.replace(/\s+/g, '_')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
        windowWidth: 900, // FIX: Forces desktop view rendering
        onclone: (clonedDoc) => {
          const modalContent = clonedDoc.getElementById('printable-consent-form');
          const scrollBody = clonedDoc.querySelector('.add-patient-consent-scroll-body');

          // FIX: Force specific width on clone to prevent flexbox collapse
          if (modalContent) {
            modalContent.style.width = '850px'; // Fixed A4-like width
            modalContent.style.maxWidth = '850px';
            modalContent.style.height = 'auto';
            modalContent.style.maxHeight = 'none';
            modalContent.style.overflow = 'visible';
            modalContent.style.boxShadow = 'none';
            modalContent.style.margin = '0';
          }

          if (scrollBody) {
            scrollBody.style.height = 'auto';
            scrollBody.style.maxHeight = 'none';
            scrollBody.style.overflow = 'visible';
            scrollBody.style.padding = '0 20px';
          }

          // FIX: Explicitly set values for inputs so they show up in PDF
          const inputs = clonedDoc.querySelectorAll('input.add-patient-consent-input-line');
          inputs.forEach(input => {
            input.setAttribute('value', input.value);
          });
        }
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last Name is required';
    if (!String(formData.status || '').trim()) newErrors.status = 'Status is required';
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.username.trim())) {
      newErrors.username = 'Username should only contain alphanumeric characters';
    }

    // Password Validation
    if (!isEditMode) {
      if (!formData.password.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const userData = { firstName: formData.firstName, lastName: formData.lastName, dob: formData.dateOfBirth };
        const validation = validatePassword(formData.password, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (formData.password && formData.password.trim()) {
      const userData = { firstName: formData.firstName, lastName: formData.lastName, dob: formData.dateOfBirth };
      const validation = validatePassword(formData.password, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    if (!isEditMode && !formData.confirmPassword.trim()) newErrors.confirmPassword = 'Confirm Password is required';
    
    const emailValue = (formData.email || '').trim();
    if (!emailValue) {
      newErrors.email = 'Email is required';
    } else if (/\s/.test(formData.email)) {
      newErrors.email = 'Email cannot contain spaces';
    } else if (!isValidEmailFormat(emailValue)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.timeZone) newErrors.timeZone = 'Time Zone is required';
    if (!formData.state) newErrors.state = 'State is required';
    const cityTrim = (formData.city || '').trim();
    if (!cityTrim) newErrors.city = 'City is required';
    else if (!/^[a-zA-Z\s'-]+$/.test(cityTrim)) {
      newErrors.city = 'City may only contain letters, spaces, hyphens, and apostrophes';
    }
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of Birth is required';
    if (!formData.addressLine1 || !formData.addressLine1.trim()) newErrors.addressLine1 = 'Address is required';
    if (formData.password && formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!termsAccepted) {
      newErrors.terms = 'You must accept the terms and conditions';
    } else if (!isEditMode) {
      if (!consentForm.patientDate || !consentForm.clinicianDate || !consentForm.caregiverDate) {
        newErrors.terms =
          'Enter all required consent signature dates (patient, clinician, and caregiver/relative).';
      } else if (isConsentDateBeforePracticeCreation(consentForm.patientDate)) {
        newErrors.terms = 'Patient signature date cannot be earlier than the practice creation date.';
      } else if (isConsentDateBeforePracticeCreation(consentForm.clinicianDate)) {
        newErrors.terms = 'Clinician signature date cannot be earlier than the practice creation date.';
      } else if (isConsentDateBeforePracticeCreation(consentForm.caregiverDate)) {
        newErrors.terms = 'Caregiver signature date cannot be earlier than the practice creation date.';
      }
    }
    if (!formData.provider || String(formData.provider).trim() === '') {
      newErrors.provider = 'Provider is required';
    }
    const zipStr = String(formData.zipCode ?? '').trim();
    if (!zipStr) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (zipStr.length < 2) {
      newErrors.zipCode = 'Zip code must be at least 2 characters';
    }
    if (!formData.rpmStartDate) newErrors.rpmStartDate = 'RPM Start Date is required';
    const homePhoneDigits = (formData.homePhone || '').replace(/\D/g, '');
    if (!homePhoneDigits) {
      newErrors.homePhone = 'Home Phone is required';
    } else if (homePhoneDigits.length < 10) {
      newErrors.homePhone = 'Invalid phone format';
    }
    // System Caregiver disabled
    // if (normalizeMultiIds(formData.systemCaregiverIds).length === 0) {
    //   newErrors.systemCaregiver = 'Select at least one system caregiver';
    // }
    if (normalizeMultiIds(formData.practiceCaregiverIds).length === 0) {
      newErrors.practiceCaregiver = 'Select at least one practice caregiver';
    }
    if (practiceMonitoringTypes.length > 0) {
      const hasMonitoringType = monitoringTypes.bloodPressure || monitoringTypes.bloodGlucose || monitoringTypes.weight;
      if (!hasMonitoringType) {
        newErrors.monitoringTypes = 'Select at least one monitoring type';
      }
    }

    const insuranceNameRegex = /^[a-zA-Z\s]+$/;
    const subscriberAlphanumeric = /^[a-zA-Z0-9]+$/;
    const insuredNameRegex = /^[a-zA-Z\s'-]+$/;
    const groupAlphanumeric = /^[a-zA-Z0-9]+$/;
    const insuranceNameRequiredMsg = 'Insurance name is required';

    const primaryNameTrim = (insuranceData.primary.insuranceName || '').trim();
    if (primaryNameTrim && !insuranceNameRegex.test(primaryNameTrim)) {
      newErrors.primaryInsuranceName = 'Insurance name: letters and spaces only';
    }

    const sec = insuranceData.secondary;
    const secNameTrim = (sec.insuranceName || '').trim();
    const secAnyFilled =
      secNameTrim ||
      String(sec.subscriberId || '').trim() ||
      String(sec.insuredName || '').trim() ||
      String(sec.groupNo || '').trim();
    if (secAnyFilled && !secNameTrim) {
      newErrors.secondaryInsuranceName = insuranceNameRequiredMsg;
    } else if (secNameTrim && !insuranceNameRegex.test(secNameTrim)) {
      newErrors.secondaryInsuranceName = insuranceNameRequiredMsg;
    }

    ['primary', 'secondary'].forEach((type) => {
      const data = insuranceData[type];
      const subTrim = (data.subscriberId || '').trim();
      if (subTrim && !subscriberAlphanumeric.test(subTrim)) {
        newErrors[`${type}SubscriberId`] = 'Subscriber ID: letters and numbers only';
      }
      const insuredTrim = (data.insuredName || '').trim();
      if (insuredTrim && !insuredNameRegex.test(insuredTrim)) {
        newErrors[`${type}InsuredName`] = 'Insured name: letters, spaces, hyphens, and apostrophes only';
      }
      const groupTrim = (data.groupNo || '').trim();
      if (groupTrim && !groupAlphanumeric.test(groupTrim)) {
        newErrors[`${type}GroupNo`] = 'Group number: letters and numbers only';
      }
    });

    if (formData.profilePicture instanceof File) {
      const allowedProfileMimes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedProfileMimes.includes(formData.profilePicture.type)) {
        newErrors.profilePicture = 'Invalid image type. Only JPG, PNG, GIF, and WebP are allowed.';
      } else if (formData.profilePicture.size >= MAX_DOCUMENT_FILE_BYTES) {
        newErrors.profilePicture = 'Profile picture must be under 10 MB.';
      }
    }

    Object.assign(newErrors, buildDocumentFieldErrors(documents));

    // Device serial: only validate fully filled meter rows (ignore empty template rows)
    const serialRegex = /^[a-zA-Z0-9-]+$/;
    meterMappings.forEach((meter, idx) => {
      if (!isCompleteMeterFormRow(meter)) return;
      const serial = String(meter.serialNumber || '').trim();
      if (!serialRegex.test(serial)) {
        newErrors[`meterSerial_${idx}`] = 'Invalid Serial (Alphanumeric and hyphen only, no spaces)';
      } else if (serial.length < 8 || serial.length > 16) {
        newErrors[`meterSerial_${idx}`] = 'Serial must be 8-16 characters';
      }
    });
    const totalErrors = Object.keys(newErrors).length;
    if (totalErrors > 0) {
      // Auto-scroll to the first error field
      setTimeout(() => {
        const firstErrorEl = document.querySelector('.add-patient-error-message');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Fallback to top if no error message found yet
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }

    setErrors(newErrors);
    return { isValid: totalErrors === 0, errors: newErrors };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validation = validateForm();
    if (validation.isValid) {
      try {
        // Map monitoring types to the format expected by backend
        const monitoringTypesArray = [];
        if (monitoringTypes.bloodPressure) {
          monitoringTypesArray.push({
            type: 'bloodPressure',
            icdCode: monitoringValues.bloodPressure || 'I50.1'
          });
        }
        if (monitoringTypes.bloodGlucose) {
          // Use default R73.0 if no ICD code is selected
          monitoringTypesArray.push({
            type: 'bloodGlucose',
            icdCode: monitoringValues.bloodGlucose && monitoringValues.bloodGlucose.trim() !== ''
              ? monitoringValues.bloodGlucose.trim()
              : 'R73.0'
          });
        }
        if (monitoringTypes.weight) {
          monitoringTypesArray.push({
            type: 'weight',
            icdCode: monitoringValues.weight || 'ICD-10'
          });
        }

        const systemCaregiverIdsNum = normalizeMultiIds(formData.systemCaregiverIds)
          .map((id) => parseInt(id, 10))
          .filter((n) => !Number.isNaN(n) && n > 0);

        const practiceCaregiverIdsNum = normalizeMultiIds(formData.practiceCaregiverIds)
          .map((id) => parseInt(id, 10))
          .filter((n) => !Number.isNaN(n) && n > 0);

        let meterRowsForPayload;
        if (isEditMode) {
          if (editingMeterId != null) {
            const first = isCompleteMeterFormRow(meterMappings[0]) ? trimMeterSubmitRow(meterMappings[0]) : null;
            const extras = meterMappings.slice(1).filter(isCompleteMeterFormRow).map(trimMeterSubmitRow);
            let base;
            if (!first) {
              base = patientMetersList
                .filter((m) => m.id !== editingMeterId)
                .map(patientListMeterToSubmitRow);
            } else {
              base = patientMetersList.map((m) =>
                m.id === editingMeterId ? first : patientListMeterToSubmitRow(m)
              );
            }
            meterRowsForPayload = dedupeMeterSubmitRows([...base, ...extras]);
          } else {
            const fromFormNew = meterMappings.filter(isCompleteMeterFormRow).map(trimMeterSubmitRow);
            const fromSaved = patientMetersList.map(patientListMeterToSubmitRow).filter(isCompleteSubmitRow);
            meterRowsForPayload = dedupeMeterSubmitRows([...fromSaved, ...fromFormNew]);
          }
        } else {
          meterRowsForPayload = meterMappings.filter(isCompleteMeterFormRow).map(trimMeterSubmitRow);
        }

        const {
          caregiver_id: _omitCaregiverId,
          practice_caregiver_id: _omitPcgId,
          practice_caregiver_ids: _omitPcgs,
          practiceCaregiverIds: _omitPcCamel,
          system_caregiver_id: _omitScgId,
          system_caregiver_ids: _omitScgs,
          systemCaregiverIds: _omitScCamel,
          ...formFieldsForSubmit
        } = formData;

        const patientData = {
          ...formFieldsForSubmit,
          state_id: formData.stateId || null,
          is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
          agreement: termsAccepted ? 1 : 0, // Add agreement/terms and conditions
          termsAndConditions: termsAccepted, // Also add camelCase version
          // Map provider and caregiver fields to backend expected names
          provider_id: formData.provider ? parseInt(formData.provider) : null,
          practice_caregiver_id: practiceCaregiverIdsNum.length > 0 ? practiceCaregiverIdsNum[0] : null,
          practice_caregiver_ids: practiceCaregiverIdsNum,
          practiceCaregiverIds: practiceCaregiverIdsNum,
          system_caregiver_id: systemCaregiverIdsNum.length > 0 ? systemCaregiverIdsNum[0] : null,
          system_caregiver_ids: systemCaregiverIdsNum,
          systemCaregiverIds: systemCaregiverIdsNum,
          // Send monitoring types in the correct format
          monitoring_types: monitoringTypesArray,
          meter_mappings: meterRowsForPayload,
          insurance_data: insuranceData,
          documents: documents,
          monitoring_values: monitoringValues,
          consent_data: consentForm
        };

        // Files are intentionally split out from the main create/update request
        // to avoid 413/timeout failures when many large medical documents are
        // attached. The profile picture stays inline (small, single image), but
        // every document file is uploaded AFTER the patient is created/updated,
        // each in its own request.
        const newDocumentFiles = documents
          .map((doc, rowIndex) => ({ doc, rowIndex }))
          .filter(
            ({ doc }) => doc.file instanceof File && doc.file.size < MAX_DOCUMENT_FILE_BYTES
          )
          .map(({ doc, rowIndex }) => ({
            file: doc.file,
            displayName: (doc.fileName && String(doc.fileName).trim()) || doc.file.name,
            rowIndex
          }));

        let submitData = patientData;
        const hasProfilePictureFile = formData.profilePicture instanceof File;
        const hasDocumentFiles = newDocumentFiles.length > 0;
        const hasMultipart = hasProfilePictureFile;

        // Edit mode: send patch payload only (changed fields).
        if (isEditMode && initialData?.apiPayload) {
          const changedPayload = diffPatientUpdatePayload(initialData.apiPayload, patientData);
          submitData = changedPayload;

          if (!patientData.password) {
            delete submitData.password;
            delete submitData.confirmPassword;
          }

          if (Object.keys(submitData).length === 0 && !hasMultipart && !hasDocumentFiles) {
            toast.info('No changes to update');
            setIsSubmitting(false);
            return;
          }
        }

        if (hasMultipart) {
          const multipartData = new FormData();
          const sourcePayload = submitData;

          // Append regular fields, excluding files which are handled separately
          Object.keys(sourcePayload).forEach(key => {
            if (key === 'profilePicture' || key === 'documents') return;

            const value = sourcePayload[key];
            if (typeof value === 'object' && value !== null) {
              multipartData.append(key, JSON.stringify(value));
            } else {
              multipartData.append(key, value !== null && value !== undefined ? value : '');
            }
          });

          if (hasProfilePictureFile) {
            multipartData.append('profilePicture', formData.profilePicture);
          }
          // NOTE: documents are NOT appended here — they go through
          // patientService.uploadPatientDocument one-by-one after the patient
          // record exists.

          submitData = multipartData;
        }

        // Full success: green toast + return to patient list.
        const handleSuccess = () => {
          practiceService.invalidatePracticePatientsCache(practiceId);
          toast.success(isEditMode ? 'Patient updated successfully' : 'Patient created successfully', {
            position: 'top-right',
            autoClose: 2000,
            onClose: () => navigate(`/practices/${practiceId}?tab=patient`)
          });
        };

        const scrollToDocumentsSection = () => {
          setTimeout(() => {
            const el = document.getElementById('add-patient-documents-section');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        };

        // Sequentially upload each document on its own request after the
        // patient row exists. Failures set `errors.documentFile_<row>` inline
        // (no duplicate 413 toasts — api.js is suppressed for this endpoint).
        const uploadDocumentsSequentially = async (patientRowId) => {
          if (!patientRowId || newDocumentFiles.length === 0) return { uploaded: 0, failed: 0 };
          let uploaded = 0;
          let failed = 0;
          for (const { file, displayName, rowIndex } of newDocumentFiles) {
            try {
              await patientService.uploadPatientDocument(
                practiceId,
                patientRowId,
                file,
                displayName
              );
              uploaded += 1;
              setErrors((prev) => {
                const next = { ...prev };
                delete next[`documentFile_${rowIndex}`];
                return next;
              });
            } catch (docErr) {
              failed += 1;
              const msg =
                docErr?.response?.data?.message ||
                'This file could not be uploaded. Each file must be under 10 MB.';
              setErrors((prev) => ({ ...prev, [`documentFile_${rowIndex}`]: msg }));
              console.error('[AddPatient] document upload failed:', docErr);
            }
          }
          return { uploaded, failed };
        };

        if (isEditMode) {
          if (!patientData.password) {
            if (hasMultipart) submitData.delete('password');
            else delete submitData.password;
          }
          await dispatch(updatePatient({ practiceId, patientId: editId, patientData: submitData })).unwrap();
          savePatientConsentDraft(practiceId, editId, consentForm);
          if (initialData?.apiPayload) {
            setInitialData((prev) => ({
              ...prev,
              apiPayload: normalizePatientUpdatePayloadForDiff(patientData)
            }));
          }
          setEditingMeterId(null);
          let docUploadResult = { uploaded: 0, failed: 0 };
          if (newDocumentFiles.length > 0) {
            docUploadResult = await uploadDocumentsSequentially(editId);
          }
          practiceService.invalidatePracticePatientsCache(practiceId);
          if (docUploadResult.failed > 0) {
            const { failed } = docUploadResult;
            toast.warning(
              `Patient was updated, but ${failed} document file(s) could not be uploaded. Fix the rows marked below and try again.`
            );
            scrollToDocumentsSection();
            setIsSubmitting(false);
            return;
          }
          handleSuccess();
        } else {
          const createModeDocErrors = buildDocumentFieldErrors(documents);
          if (Object.keys(createModeDocErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...createModeDocErrors }));
            setShowValidationModal(true);
            scrollToDocumentsSection();
            setIsSubmitting(false);
            return;
          }

          const createResult = await dispatch(createPatient({ practiceId, patientData: submitData })).unwrap();

          // Upload documents (one per request) using the newly-created patient's
          // patients.id from the create response.
          const newPatientRowId = createResult?.data?.patient_table_id ?? createResult?.patient_table_id;
          if (newPatientRowId) {
            savePatientConsentDraft(practiceId, newPatientRowId, consentForm);
          }
          let docUploadResult = { uploaded: 0, failed: 0 };
          if (newPatientRowId && newDocumentFiles.length > 0) {
            docUploadResult = await uploadDocumentsSequentially(newPatientRowId);
          } else if (!newPatientRowId && newDocumentFiles.length > 0) {
            toast.warning(
              'Patient was created but the server did not return a patient row id, so document uploads were skipped. Please open the patient and upload the files manually.'
            );
          }

          try {
            const notificationSettings = {
              email_notifications_for_superadmin: currentPractice?.email_notifications_for_superadmin === 1,
              email_notifications_for_provider: currentPractice?.email_notifications_for_provider === 1,
              email_notifications_for_practice_caregiver: currentPractice?.email_notifications_for_practice_caregiver === 1,
              email_notifications_for_system_caregiver: currentPractice?.email_notifications_for_system_caregiver === 1
            };

            await sendPatientAdditionNotifications(
              createResult.data,
              currentPractice,
              notificationSettings
            );
          } catch (emailError) {
            console.error('Patient notification error:', emailError);
          }

          practiceService.invalidatePracticePatientsCache(practiceId);
          if (docUploadResult.failed > 0 && newPatientRowId) {
            const { failed } = docUploadResult;
            toast.warning(
              `Patient was created, but ${failed} document file(s) could not be saved. Fix the issues in the document section and try again (each file must be under 10 MB).`
            );
            setIsSubmitting(false);
            navigate(`/practices/${practiceId}/patients/${newPatientRowId}/edit`);
            return;
          }
          if (docUploadResult.failed > 0 && !newPatientRowId) {
            setIsSubmitting(false);
            return;
          }
          handleSuccess();
        }
      } catch (error) {
        console.error('Error submitting:', error);

        // Prioritize server response message over generic error message
        // The Redux thunk returns the error message string directly via rejectWithValue
        const errorMessage =
          typeof error === 'string'
            ? error
            : error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Failed to save patient.';

        // Handle specific field errors based on server message content
        if (errorMessage) {
          const lowerMsg = String(errorMessage).toLowerCase();

          if (lowerMsg.includes('username') && (lowerMsg.includes('exist') || lowerMsg.includes('taken'))) {
            setErrors(prev => ({ ...prev, username: errorMessage }));
            // Focus and scroll to the username field
            setTimeout(() => {
              const usernameField = document.getElementById('username');
              if (usernameField) {
                usernameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                usernameField.focus({ preventScroll: true });
              }
            }, 100);
          } else if (lowerMsg.includes('email') && (lowerMsg.includes('exist') || lowerMsg.includes('taken'))) {
            setErrors(prev => ({ ...prev, email: errorMessage }));
            setTimeout(() => {
              const emailField = document.getElementById('email');
              if (emailField) {
                emailField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                emailField.focus({ preventScroll: true });
              }
            }, 100);
          } else if (lowerMsg.includes('different from current password')) {
            setErrors(prev => ({ ...prev, password: errorMessage }));
            setTimeout(() => {
              const passwordField = document.getElementById('password');
              if (passwordField) {
                passwordField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                passwordField.focus({ preventScroll: true });
              }
            }, 100);
          } else if (
            lowerMsg.includes('serial') &&
            (lowerMsg.includes('assigned') ||
              lowerMsg.includes('another') ||
              lowerMsg.includes('registered') ||
              lowerMsg.includes('inventory'))
          ) {
            const quoted = String(errorMessage).match(/"([^"]+)"/);
            const conflictSn = quoted ? quoted[1] : null;
            setErrors((prev) => {
              const next = { ...prev };
              if (conflictSn) {
                meterMappings.forEach((row, idx) => {
                  if (String(row.serialNumber || '').trim() === conflictSn) {
                    next[`meterSerial_${idx}`] = errorMessage;
                  }
                });
              } else {
                meterMappings.forEach((row, idx) => {
                  if (isCompleteMeterFormRow(row)) {
                    next[`meterSerial_${idx}`] = errorMessage;
                  }
                });
              }
              return next;
            });
            setTimeout(() => {
              const el = document.querySelector('.add-patient-form-control.is-invalid');
              el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
          }
        }

        // Always apply submit error to state (using functional update to preserve other errors)
        setErrors(prev => ({ ...prev, submit: errorMessage }));

        // Always show toast
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
        });

      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
      setShowValidationModal(true);
      
      // Scroll to the first error field
      const firstErrorKey = Object.keys(validation.errors)[0];
      if (firstErrorKey) {
        const element = document.getElementById(firstErrorKey);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus({ preventScroll: true });
        }
      }
    }
  };

  const openPatientDocument = (doc) => {
    const file = doc?.file_name ?? doc?.document ?? doc?.filename;
    if (!file || typeof file !== 'string') {
      toast.error('No file path for this document');
      return;
    }
    window.open(`/uploads/patient-documents/${encodeURIComponent(file)}`, '_blank', 'noreferrer');
  };

  const handleRenamePatientDocument = async (doc) => {
    if (!practiceId || !editId || doc?.id == null) return;
    const current = String(doc.document_name || doc.name || '').trim();
    const next = window.prompt('Document display name', current);
    if (next == null) return;
    const name = String(next).trim();
    if (!name) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      const res = await patientService.patchPatientDocument(practiceId, editId, doc.id, { name });
      if (res.data?.success) {
        toast.success('Document name updated');
        setPatientDocumentsList((prev) =>
          prev.map((row) => (row.id === doc.id ? { ...row, name, document_name: name } : row))
        );
      } else {
        toast.error(res.data?.message || 'Update failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const openDeleteDocumentConfirm = (doc) => {
    if (!practiceId || !editId || doc?.id == null) return;
    setDeleteDocumentConfirm(doc);
  };

  const handleDeletePatientDocumentConfirmed = async () => {
    const doc = deleteDocumentConfirm;
    if (!doc || doc.id == null) return;
    const previousList = patientDocumentsList;
    setDeleteDocumentConfirm(null);
    setPatientDocumentsList((prev) => prev.filter((row) => row.id !== doc.id));
    toast.success('Document removed');
    try {
      const res = await patientService.deletePatientDocument(practiceId, editId, doc.id);
      if (!res.data?.success) {
        setPatientDocumentsList(previousList);
        toast.error(res.data?.message || 'Delete failed');
      }
    } catch (err) {
      setPatientDocumentsList(previousList);
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const practiceLogoUrl = useMemo(
    () => resolvePracticeLogoUrl(currentPractice?.practice_logo, {
      cacheBust: currentPractice?.updated_at || currentPractice?.id || 'practice-logo'
    }),
    [currentPractice?.practice_logo, currentPractice?.updated_at, currentPractice?.id]
  );

  if (isEditMode && isLoadingPatientData) {
    return (
      <div className="add-patient-container" id="add-patient-main">
        <div className="add-patient-form-card" style={{ padding: '16px', display: 'grid', gap: '14px' }}>
          <FormDetailSkeleton titleLines={1} fieldRows={10} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={4} columns={2} showFooter={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="add-patient-container" id="add-patient-main">
      <div className="ap-practice-header">
        <h3 className="ap-practice-name">{currentPractice?.practice_name || 'Practice'}</h3>
        {practiceLogoUrl && (
          <img
            src={practiceLogoUrl}
            alt="Practice Logo"
            className="ap-practice-logo"
          />
        )}
      </div>


      {showConsentModal && (
        <div className="add-patient-modal-overlay">
          <div className="add-patient-modal-content" id="printable-consent-form">
            <button className="add-patient-close-btn-corner" onClick={() => setShowConsentModal(false)} data-html2canvas-ignore="true">
              <FontAwesomeIcon icon={faTimes} />
            </button>

            {practiceLogoUrl && !consentLogoFailed ? (
              <div className="add-patient-consent-logo-header">
                <img
                  src={practiceLogoUrl}
                  alt=""
                  className="add-patient-consent-practice-logo"
                  referrerPolicy="no-referrer"
                  onError={() => setConsentLogoFailed(true)}
                />
              </div>
            ) : null}

            <div className="add-patient-consent-title-section">
              <span className="add-patient-doc-title">PATIENT CONSENT FORM</span>
              <span className="add-patient-doc-subtitle">Remote Physiological Monitoring</span>
            </div>

            <div className="add-patient-consent-scroll-body">
              <div className="add-patient-doc-section">
                <h5 className="add-patient-doc-section-title">Patient Details:</h5>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>Name :</label>
                    <input type="text" name="name" className="add-patient-consent-input-line" value={consentForm.name} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>DOB :</label>
                    <input type="date" name="dob" className="add-patient-consent-input-line" value={consentForm.dob} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>Tel :</label>
                    <input type="text" name="tel" className="add-patient-consent-input-line" value={consentForm.tel} onChange={handleConsentChange} />
                  </div>
                </div>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>Address :</label>
                    <input type="text" name="address" className="add-patient-consent-input-line" value={consentForm.address} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>Email <span className="add-patient-required">*</span> :</label>
                    <input type="email" name="email" className="add-patient-consent-input-line" value={consentForm.email} onChange={handleConsentChange} />
                  </div>
                </div>
              </div>

              <div className="add-patient-doc-section">
                <h5 className="add-patient-doc-section-title uppercase">MEDICAL DEVICES SUPPLIED TO PATIENT</h5>
                <p className="add-patient-doc-text-intro">
                  By signing the Patient Consent Form, I hereby consent to Doctors Name with Practice providing Remote Physiological Monitoring (RPM) services to me and agree to the following.
                </p>
                <ul className="add-patient-doc-list">
                  <li>The medical Devices provided to me are the property of the Provider. I will not tamper with the Medical Device and I understand that I am responsible for any cost and expenses related to the misuse of the Medical Device.</li>
                  <li>I will be the only one to use the medical device as instructed by the provider and I will not use the device for reasons other than health monitoring.</li>
                  <li>The medical device is to be used solely in connection with the RPM Services and associated RPM program provided by the provider and not for any other purpose.</li>
                  <li>THE RPM PROGRAM IS NOT A REPLACEMENT FOR EMERGENCY SERVICE. CALL 9-1-1 FOR IMMEDIATE MEDICAL EMERGENCIES AND DO NOT USE THE MEDICAL DEVICE IN REPLACEMENT OF THE 9-1-1 SERVICE.</li>
                  <li>The length of time I am on the RPM program will be determined by the provider at its sole discretion and I may be removed from the RPM program at any time.</li>
                  <li>My data and medical information obtained from my participation in the RPM program may /may not be considered part of my patient record, but may be shared with other health care professionals to enhance my care and I authorize such professional disclosure. My data and information will be securely transmitted data and reviewed at the clinician's discretion.</li>
                  <li>I will comply with the RPM program clinical expectations and if I do not, I may be removed from the RPM program and I will return the medical device.</li>
                  <li>I have the rights to withdraw my consent to participate in the RPM program and stop receiving RPM Service at any time.</li>
                </ul>

                <p className="add-patient-doc-text-intro" style={{ marginTop: '15px' }}><strong>Provider's obligation:</strong> When providing the RPM Services, the Provider must</p>
                <ul className="add-patient-doc-list">
                  <li>Clearly explain the detail of the RPM Service to be provided to you and that are applicable to your conditions.</li>
                  <li>Provide you with a written copy of your care plan.</li>
                  <li>Provide you with a written confirmation of your revocation from receiving the RPM Services and participating in the RPM if you revoke your consent.</li>
                </ul>
              </div>

              <div className="add-patient-doc-section">
                <h5 className="add-patient-doc-section-title">Patient Consent:</h5>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>Patient Signature :</label>
                    <input type="text" name="patientSignature" className="add-patient-consent-input-line" value={consentForm.patientSignature} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>Date <span className="add-patient-required">*</span> :</label>
                    <input
                      type="date"
                      name="patientDate"
                      className="add-patient-consent-input-line"
                      value={consentForm.patientDate}
                      onChange={handleConsentChange}
                      onClick={(e) => { try { e.target.showPicker(); } catch (err) {} }}
                      min={practiceCreatedAtStr || undefined}
                      required
                      title={
                        practiceCreatedAtStr
                          ? `Select a date on or after ${practiceCreatedAtStr}`
                          : 'Select a date'
                      }
                    />
                  </div>
                </div>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>Clinician Signature :</label>
                    <input type="text" name="clinicianSignature" className="add-patient-consent-input-line" value={consentForm.clinicianSignature} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>Date <span className="add-patient-required">*</span> :</label>
                    <input
                      type="date"
                      name="clinicianDate"
                      className="add-patient-consent-input-line"
                      value={consentForm.clinicianDate}
                      onChange={handleConsentChange}
                      onClick={(e) => { try { e.target.showPicker(); } catch (err) {} }}
                      min={practiceCreatedAtStr || undefined}
                      required
                      title={
                        practiceCreatedAtStr
                          ? `Select a date on or after ${practiceCreatedAtStr}`
                          : 'Select a date'
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="add-patient-doc-section">
                <p className="add-patient-doc-text-small">
                  In the event that the patient is unable to give consent due to mental or other capacity issues, the relative, or caregiver who has Lasting/Durable Power of Attorney for the health and welfare responsibly, may give consent if deemed to be in the best interest of the patient. In these instances, the caregiver/relative should sign below.
                </p>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field full-width">
                    <label>CareGiver/relative name :</label>
                    <input type="text" name="caregiverName" className="add-patient-consent-input-line" value={consentForm.caregiverName} onChange={handleConsentChange} />
                  </div>
                </div>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>CareGiver/relative signature :</label>
                    <input type="text" name="caregiverSignature" className="add-patient-consent-input-line" value={consentForm.caregiverSignature} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>
                      Date <span className="add-patient-required">*</span> :
                    </label>
                    <input
                      type="date"
                      name="caregiverDate"
                      className={`add-patient-consent-input-line ${!consentForm.caregiverDate ? 'is-invalid' : ''}`}
                      value={consentForm.caregiverDate}
                      onChange={handleConsentChange}
                      onClick={(e) => { try { e.target.showPicker(); } catch (err) {} }}
                      min={practiceCreatedAtStr || undefined}
                      required
                      title={
                        practiceCreatedAtStr
                          ? `Select a date on or after ${practiceCreatedAtStr}`
                          : 'Select a date'
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="add-patient-doc-footer-buttons" data-html2canvas-ignore="true">
              <button className="add-patient-btn-doc-print" onClick={handleDownloadPDF}>
                Print / Download PDF
              </button>
              <button
                className="add-patient-btn-doc-agree"
                onClick={handleAgreeToConsent}
                disabled={
                  !consentForm.patientDate || !consentForm.clinicianDate || !consentForm.caregiverDate
                }
                title={
                  !consentForm.patientDate || !consentForm.clinicianDate || !consentForm.caregiverDate
                    ? 'Enter patient, clinician, and caregiver/relative signature dates before agreeing.'
                    : ''
                }
              >
                Agree
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="add-patient-form-card">
        <div className="add-patient-form-header">
          <div>
            <h4>{isEditMode ? 'Edit patient' : 'Add Practice Patient'}</h4>
          </div>
        </div>
        <div className="add-patient-account-row">
          {accountNumber !== null ? (
            <div className="add-patient-account-number">
              Account #: {accountNumber}
            </div>
          ) : (
            <div className="add-patient-account-loading">
              {isEditMode ? 'Loading account info...' : 'Generating account number...'}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="add-patient-provider-form" noValidate>
          <h5 className="add-patient-block-title">Patient information</h5>
          <div className="add-patient-block">
            {/* Row Pair 1: Title/First + Middle/Last(L) - Username(R) */}
            <div className="add-patient-row-pair">
              <div className="add-patient-col-half">
                <div className="d-flex gap-3 mb-3">
                  <div className="add-patient-form-group" style={{ flex: '0 0 100px', minWidth: '100px' }}>
                    <label htmlFor="title">Title</label>
                    <select
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="add-patient-form-control"
                    >
                      <option value="">- Select -</option>
                      <option value="1">Dr.</option>
                      <option value="2">Mr.</option>
                      <option value="3">Mrs.</option>
                      <option value="4">Ms.</option>
                    </select>
                    {errors.title && <div className="add-patient-error-message">{errors.title}</div>}
                  </div>
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="firstName">
                      First Name <span className="add-patient-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className={`add-patient-form-control ${errors.firstName ? 'is-invalid' : ''}`}
                    />
                    {errors.firstName && <div className="add-patient-error-message">{errors.firstName}</div>}
                  </div>
                </div>
              </div>

              <div className="add-patient-col-half">
                <div className="add-patient-form-group">
                  <label htmlFor="username">
                    Username <span className="add-patient-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className={`add-patient-form-control ${errors.username ? 'is-invalid' : ''}`}
                  />
                  {errors.username && <div className="add-patient-error-message">{errors.username}</div>}
                </div>
              </div>
            </div>

            {/* Row Pair 2: Middle/Last(L) - Password(R) */}
            <div className="add-patient-row-pair">
              <div className="add-patient-col-half">
                <div className="d-flex gap-3">
                  <div className="add-patient-form-group" style={{ flex: '0 0 100px' }}>
                    <label htmlFor="middleInitial">Middle Initial</label>
                    <input
                      type="text"
                      id="middleInitial"
                      name="middleInitial"
                      value={formData.middleInitial}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key === ' ') {
                          e.preventDefault();
                        }
                      }}
                      className={`add-patient-form-control ${errors.middleInitial ? 'is-invalid' : ''}`}
                    />
                  </div>
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="lastName">
                      Last Name <span className="add-patient-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className={`add-patient-form-control ${errors.lastName ? 'is-invalid' : ''}`}
                    />
                    {errors.lastName && <div className="add-patient-error-message">{errors.lastName}</div>}
                  </div>
                </div>
              </div>

              <div className="add-patient-col-half">
                <div className="add-patient-form-group">
                  <label htmlFor="password">
                    Password {!isEditMode && <span className="add-patient-required">*</span>}
                    <OverlayTrigger placement="top" overlay={<Tooltip>8-64 characters, 1 uppercase, 1 lowercase, 1 number, 1 special char.</Tooltip>}>
                      <FontAwesomeIcon icon={faInfoCircle} className="field-info-icon" />
                    </OverlayTrigger>
                  </label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      required={!isEditMode}
                      className={`add-patient-form-control ${errors.password ? 'is-invalid' : ''}`}
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="add-patient-password-toggle"
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {errors.password && <div className="add-patient-error-message">{errors.password}</div>}
                </div>
              </div>
            </div>

            {/* Row Pair 3: Addr1(L) - Confirm Password(R) */}
            <div className="add-patient-row-pair">
              <div className="add-patient-col-half">
                <div className="add-patient-form-group">
                  <label htmlFor="addressLine1">
                    Address Line 1 <span className="add-patient-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="addressLine1"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    required
                    className={`add-patient-form-control ${errors.addressLine1 ? 'is-invalid' : ''}`}
                  />
                  {errors.addressLine1 && <div className="add-patient-error-message">{errors.addressLine1}</div>}
                </div>
              </div>

              <div className="add-patient-col-half">
                <div className="add-patient-form-group">
                  <label htmlFor="confirmPassword">
                    Confirm Password {!isEditMode && <span className="add-patient-required">*</span>}
                  </label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      required={!isEditMode}
                      className={`add-patient-form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="add-patient-password-toggle"
                    >
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {errors.confirmPassword && <div className="add-patient-error-message">{errors.confirmPassword}</div>}
                </div>
              </div>
            </div>

            {/* Row Pair 4: Addr2(L) - Fax(R) */}
            <div className="add-patient-row-pair">
              <div className="add-patient-col-half">
                <div className="add-patient-form-group">
                  <label htmlFor="addressLine2">Address Line 2</label>
                  <input
                    type="text"
                    id="addressLine2"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleInputChange}
                    className="add-patient-form-control"
                  />
                </div>
              </div>

              <div className="add-patient-col-half">
                <div className="add-patient-form-group">
                  <label htmlFor="fax">
                    Fax
                    <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                      <FontAwesomeIcon icon={faInfoCircle} className="field-info-icon" />
                    </OverlayTrigger>
                  </label>
                  <input
                    type="tel"
                    id="fax"
                    name="fax"
                    value={formData.fax}
                    onChange={handleInputChange}
                    className="add-patient-form-control"
                  />
                </div>
              </div>
            </div>

            {/* Row Pair 5: City/State/Zip(L) - Two Way Auth(R) */}
            <div className="add-patient-row-pair">
              <div className="add-patient-col-half">
                <div className="d-flex gap-3">
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="city">
                      City <span className="add-patient-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className={`add-patient-form-control ${errors.city ? 'is-invalid' : ''}`}
                    />
                    {errors.city && <div className="add-patient-error-message">{errors.city}</div>}
                  </div>
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="state">
                      State <span className="add-patient-required">*</span>
                    </label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      className={`add-patient-form-control ${errors.state ? 'is-invalid' : ''}`}
                    >
                      <option value="">- Select -</option>
                      {US_STATES.map(state => (
                        <option key={state.id} value={state.name}>{state.name}</option>
                      ))}
                    </select>
                    {errors.state && <div className="add-patient-error-message">{errors.state}</div>}
                  </div>
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="zipCode">
                      Zip Code <span className="add-patient-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      required
                      className={`add-patient-form-control ${errors.zipCode ? 'is-invalid' : ''}`}
                    />
                    {errors.zipCode && <div className="add-patient-error-message">{errors.zipCode}</div>}
                  </div>
                </div>
              </div>

              <div className="add-patient-col-half">
                <div className="add-patient-form-group flex-grow-1">
                  <label htmlFor="twoWayAuth">Two Way Authentication <span className="add-patient-required">*</span></label>
                  <select
                    id="twoWayAuth"
                    name="twoWayAuth"
                    value={formData.twoWayAuth}
                    onChange={handleInputChange}
                    className="add-patient-form-control"
                  >
                    <option value="Enabled">Enabled</option>
                    <option value="Disabled">Disabled</option>
                  </select>
                  {errors.twoWayAuth && <div className="add-patient-error-message">{errors.twoWayAuth}</div>}
                </div>
              </div>
            </div>

            {/* Row Pair 6: Email(L) - Status(R) */}
            <div className="add-patient-row-pair">
              <div className="add-patient-col-half">
                <div className="add-patient-form-group">
                  <label htmlFor="email">
                    Email <span className="add-patient-required">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    spellCheck={false}
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const v = String(e.target.value ?? '').replace(/\s/g, '');
                      handleInputChange({ target: { name: 'email', value: v } });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === ' ') {
                        e.preventDefault();
                      }
                    }}
                    required
                    className={`add-patient-form-control ${
                      (() => {
                        const t = (formData.email || '').trim();
                        const badFormat = t.length > 0 && !isValidEmailFormat(t);
                        return badFormat || !!errors.email;
                      })()
                        ? 'is-invalid'
                        : ''
                    }`}
                  />
                  {(() => {
                    const t = (formData.email || '').trim();
                    if (t && !isValidEmailFormat(t)) {
                      return <div className="add-patient-error-message">Invalid email format</div>;
                    }
                    if (errors.email) {
                      return <div className="add-patient-error-message">{errors.email}</div>;
                    }
                    return null;
                  })()}
                </div>
              </div>

              <div className="add-patient-col-half">
                <div className="add-patient-form-group flex-grow-1">
                  <label htmlFor="status">Status <span className="add-patient-required">*</span></label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                    className={`add-patient-form-control ${errors.status ? 'is-invalid' : ''}`}
                  >
                    <option value="Active">Active</option>
                    <option value="Pending">Pending</option>
                    <option value="Locked">Locked</option>
                  </select>
                  {errors.status && <div className="add-patient-error-message">{errors.status}</div>}
                </div>
              </div>
            </div>

            {/* Row Pair 7: Home Phone/Cell Phone(L) - Time Zone/RPM Start Date(R) */}
            <div className="add-patient-row-pair">
              <div className="add-patient-col-half">
                <div className="d-flex gap-3">
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="homePhone">
                      Home Phone <span className="add-patient-required">*</span>
                      <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                        <FontAwesomeIcon icon={faInfoCircle} className="field-info-icon" />
                      </OverlayTrigger>
                    </label>
                    <input
                      type="tel"
                      id="homePhone"
                      name="homePhone"
                      value={formData.homePhone}
                      onChange={handleInputChange}
                      required
                      className={`add-patient-form-control ${errors.homePhone ? 'is-invalid' : ''}`}
                    />
                    {errors.homePhone && <div className="add-patient-error-message">{errors.homePhone}</div>}
                  </div>
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="cellPhone">Cell Phone</label>
                    <input
                      type="tel"
                      id="cellPhone"
                      name="cellPhone"
                      value={formData.cellPhone}
                      onChange={handleInputChange}
                      className="add-patient-form-control"
                    />
                  </div>
                </div>
              </div>

              <div className="add-patient-col-half">
                <div className="d-flex gap-3 mb-3">
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="timeZone">Time Zone <span className="add-patient-required">*</span></label>
                    <select
                      id="timeZone"
                      name="timeZone"
                      value={formData.timeZone}
                      onChange={handleInputChange}
                      className={`add-patient-form-control ${errors.timeZone ? 'is-invalid' : ''}`}
                    >
                      <option value="">- Select -</option>
                      {TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                    {errors.timeZone && <div className="add-patient-error-message">{errors.timeZone}</div>}
                  </div>
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="rpmStartDate">RPM Start Date <span className="add-patient-required">*</span></label>
                    <input
                      type="date"
                      id="rpmStartDate"
                      name="rpmStartDate"
                      value={formData.rpmStartDate}
                      onChange={handleInputChange}
                      onClick={(e) => { try { e.target.showPicker(); } catch (err) {} }}
                      className={`add-patient-form-control ${errors.rpmStartDate ? 'is-invalid' : ''}`}
                    />
                    {errors.rpmStartDate && <div className="add-patient-error-message">{errors.rpmStartDate}</div>}
                  </div>
                </div>
              </div>
            </div>

            {/* Row Pair 8: Gender/DOB(L) - Provider/System caregivers(R) */}
            <div className="add-patient-row-pair">
              <div className="add-patient-col-half">
                <div className="d-flex gap-3 mb-3">
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="gender">Gender <span className="add-patient-required">*</span></label>
                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="add-patient-form-control"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="add-patient-form-group flex-grow-1">
                    <label htmlFor="dateOfBirth">Date of Birth <span className="add-patient-required">*</span></label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      onClick={(e) => { try { e.target.showPicker(); } catch (err) {} }}
                      className={`add-patient-form-control ${errors.dateOfBirth ? 'is-invalid' : ''}`}
                    />
                    {errors.dateOfBirth && <div className="add-patient-error-message">{errors.dateOfBirth}</div>}
                  </div>
                </div>
              </div>

              <div className="add-patient-col-half">
                <div className="add-patient-caregivers-fields">
                  <div className="d-flex gap-3 mb-3">
                    <div className="flex-grow-1">
                      <PatientAscStyleSinglePicker
                        label="Provider"
                        name="provider"
                        required
                        value={formData.provider}
                        options={activePracticeProviders}
                        error={errors.provider}
                        placeholder="Select provider"
                        emptyMessage="No providers available"
                        onPick={pickAscField}
                        id="provider"
                      />
                    </div>
                    {/* System Caregiver picker disabled
                    <div className="flex-grow-1">
                      <PatientAscStyleMultiPicker
                        label="System caregivers"
                        required
                        selectedIds={formData.systemCaregiverIds}
                        options={systemCaregiverPickerOptions}
                        onAddId={addSystemCaregiverId}
                        onRemoveId={removeSystemCaregiverId}
                        error={errors.systemCaregiver}
                        placeholder="Select system caregivers"
                        emptyMessage="No system caregivers available"
                        id="systemCaregiver"
                      />
                    </div>
                    */}
                  </div>
                </div>
              </div>
            </div>

            {/* Row Pair 9: Upload(L) - Practice caregivers(R) */}
            <div className="add-patient-row-pair">
              <div className="add-patient-col-half">
                <div className="add-patient-form-group">
                  <label>Profile picture</label>
                  <div className="add-patient-upload-picture-row">
                    <div className={`add-patient-upload-picture-filename ${errors.profilePicture ? 'is-invalid' : ''}`}>
                      {profileFileName || 'No file selected'}
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleProfilePictureUpload}
                    />
                    <button type="button" className="add-patient-btn-upload-picture-compact" onClick={triggerProfileUpload}>
                      <FontAwesomeIcon icon={faCloudUploadAlt} /> Upload picture
                    </button>
                  </div>
                  {profilePreview ? (
                    <div className="add-patient-profile-preview-large-wrap">
                      <img src={profilePreview} alt="Profile preview" className="add-patient-profile-preview-large" />
                    </div>
                  ) : null}
                  {errors.profilePicture && (
                    <div className="add-patient-error-message">{errors.profilePicture}</div>
                  )}
                </div>
              </div>

              <div className="add-patient-col-half">
                <div className="mb-2">
                  <PatientAscStyleMultiPicker
                    label="Practice caregivers"
                    required
                    selectedIds={formData.practiceCaregiverIds}
                    options={practiceCaregiverPickerOptions}
                    onAddId={addPracticeCaregiverId}
                    onRemoveId={removePracticeCaregiverId}
                    error={errors.practiceCaregiver}
                    placeholder="Select practice caregivers"
                    emptyMessage="No practice caregivers available"
                    id="practiceCaregiver"
                  />
                </div>
              </div>
            </div>

          </div> {/* Closes add-patient-block for Patient Profile Information */}

          <h5 className="add-patient-block-title">Meter mapping</h5>
          <div className="add-patient-block">
            <div className="add-patient-section-content">
              {meterMappings.map((mapping, index) => (
                <div
                  key={mapping.id != null ? `meter-${mapping.id}` : `meter-new-${index}`}
                  className="add-patient-meter-mapping-row"
                >
                  <div className="add-patient-form-group">
                    <label>Model number</label>
                    <select
                      value={mapping.productId != null ? String(mapping.productId) : ''}
                      onChange={(e) => handleMeterProductChange(index, e.target.value)}
                      className="add-patient-form-control"
                      disabled={loadingProducts}
                    >
                      <option value="">- Select -</option>
                      {products.map((product) => (
                        <option key={product.id} value={String(product.id)}>
                          {product.model_number}
                          {product.product_name ? ` (${product.product_name})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="add-patient-form-group">
                    <label>Extension</label>
                    <select
                      value={
                        METER_EXTENSION_OPTIONS.some((o) => String(o.value) === String(mapping.extension))
                          ? String(mapping.extension)
                          : ''
                      }
                      onChange={(e) => updateMeterMapping(index, 'extension', e.target.value)}
                      className="add-patient-form-control"
                    >
                      <option value="">- Select -</option>
                      {METER_EXTENSION_OPTIONS.map((ext) => (
                        <option key={ext.value} value={ext.value}>
                          {ext.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="add-patient-form-group">
                    <label>Serial number</label>
                    <input
                      id={`meterSerial_${index}`}
                      type="text"
                      value={mapping.serialNumber || ''}
                      onChange={(e) => {
                        patchMeterMapping(index, {
                          serialNumber: e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 16)
                        });
                        clearMeterSerialError(index);
                      }}
                      onBlur={() => validateMeterSerialWithServer(index)}
                      className={`add-patient-form-control ${errors[`meterSerial_${index}`] ? 'is-invalid' : ''}`}
                      placeholder={mapping.productId ? 'Enter serial number' : ''}
                      title={errors[`meterSerial_${index}`] || 'Enter serial number (8–16 characters: letters, numbers, hyphen)'}
                      maxLength={16}
                      disabled={!mapping.productId}
                    />
                    {errors[`meterSerial_${index}`] && (
                      <div className="add-patient-error-message">{errors[`meterSerial_${index}`]}</div>
                    )}
                  </div>
                  <div className="add-patient-form-group">
                    <label>Gateway serial number</label>
                    <input
                      type="text"
                      value={mapping.gatewaySerialNumber}
                      onChange={(e) => updateMeterMapping(index, 'gatewaySerialNumber', e.target.value)}
                      className="add-patient-form-control"
                    />
                  </div>
                  <div className="add-patient-action-group">
                    <label>&nbsp;</label>
                    {index === meterMappings.length - 1 ? (
                      <button type="button" className="add-patient-btn-icon-dark" onClick={addMeterMapping}>
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    ) : (
                      <button type="button" className="add-patient-btn-icon-danger" onClick={() => removeMeterMapping(index)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {isEditMode && editingMeterId != null && (
                <div className="alert alert-info py-2 px-3 mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>Editing one device above. Use <strong>Update</strong> to save, or <strong>Cancel edit</strong> to discard.</span>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleCancelMeterEdit}>
                    Cancel edit
                  </button>
                </div>
              )}
            </div>

            {/* Mapped Devices - Only in Edit Mode, directly after Meter Mapping */}
            {isEditMode && (
              <div className="add-patient-section-wrapper mt-4 add-patient-section-wrapper--in-block">
                <div className="add-patient-section-header d-flex justify-content-between align-items-center">
                  <h3 className="m-0">Mapped Devices</h3>
                </div>
                <div className="add-patient-section-content p-0 add-patient-inline-table-wrap">
                  <table className="table table-hover mb-0 add-patient-inline-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Device make</th>
                        <th>Model no.</th>
                        <th>Extension</th>
                        <th>Serial no.</th>
                        <th>Gateway no.</th>
                        <th>Date assigned</th>
                        <th className="add-patient-table-actions-head">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patientMetersList.length > 0 ? (
                        patientMetersList.map((meter, idx) => (
                          <tr key={meter.id != null ? `meter-${meter.id}` : `meter-list-${idx}`}>
                            <td>{idx + 1}</td>
                            <td>{meter.product_name || '—'}</td>
                            <td className="text-break">{meter.model_number || '—'}</td>
                            <td>{meter.extension || '—'}</td>
                            <td className="text-break">{meter.serial_number || '—'}</td>
                            <td className="text-break">{meter.gateway_serial_number || '—'}</td>
                            <td>{meter.created_at ? new Date(meter.created_at).toLocaleDateString() : '—'}</td>
                            <td className="add-patient-table-actions-cell">
                              <ActionButtons
                                hideView
                                onEdit={() => handleEditMappedMeter(meter)}
                                onDelete={() => openDeleteMeterConfirm(meter)}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">No mapped devices found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <h5 className="add-patient-block-title">Monitoring type <span className="add-patient-required">*</span></h5>
          <div className={`add-patient-block ${errors.monitoringTypes ? 'add-patient-block-invalid' : ''}`}>
            <div className="add-patient-section-content add-patient-monitoring-content">
              {practiceMonitoringTypes.includes(1) && (
                <div className="add-patient-monitoring-row">
                  <div className="add-patient-monitoring-check-col">
                    <input type="checkbox" id="checkBP" checked={monitoringTypes.bloodPressure} onChange={() => handleMonitoringTypeChange('bloodPressure')} />
                    <label htmlFor="checkBP">Blood Pressure</label>
                  </div>
                  <div className="add-patient-monitoring-select-col">
                    <select className={`add-patient-form-control ${!monitoringTypes.bloodPressure ? 'add-patient-disabled-select' : ''}`} disabled={!monitoringTypes.bloodPressure} value={monitoringValues.bloodPressure} onChange={(e) => handleMonitoringValueChange('bloodPressure', e.target.value)}>
                      <option value="I50.1">I50.1</option>
                      <option value="I50.2">I50.2</option>
                      <option value="I50.3">I50.3</option>
                    </select>
                  </div>
                </div>
              )}
              {practiceMonitoringTypes.includes(2) && (
                <div className="add-patient-monitoring-row">
                  <div className="add-patient-monitoring-check-col">
                    <input type="checkbox" id="checkBG" checked={monitoringTypes.bloodGlucose} onChange={() => handleMonitoringTypeChange('bloodGlucose')} />
                    <label htmlFor="checkBG">Blood Glucose</label>
                  </div>
                  <div className="add-patient-monitoring-select-col">
                    <select className={`add-patient-form-control ${!monitoringTypes.bloodGlucose ? 'add-patient-disabled-select' : ''}`} disabled={!monitoringTypes.bloodGlucose} value={monitoringValues.bloodGlucose} onChange={(e) => handleMonitoringValueChange('bloodGlucose', e.target.value)}>
                      <option value="R73.0">R73.0</option>
                      <option value="R73.01">R73.01</option>
                      <option value="R73.02">R73.02</option>
                    </select>
                  </div>
                </div>
              )}
              {practiceMonitoringTypes.includes(3) && (
                <div className="add-patient-monitoring-row">
                  <div className="add-patient-monitoring-check-col">
                    <input type="checkbox" id="checkWeight" checked={monitoringTypes.weight} onChange={() => handleMonitoringTypeChange('weight')} />
                    <label htmlFor="checkWeight">Weight</label>
                  </div>
                  <div className="add-patient-monitoring-select-col">
                    <select className={`add-patient-form-control ${!monitoringTypes.weight ? 'add-patient-disabled-select' : ''}`} disabled={!monitoringTypes.weight} value={monitoringValues.weight} onChange={(e) => handleMonitoringValueChange('weight', e.target.value)}>
                      <option value="ICD-10">ICD-10</option>
                      <option value="ICD-9">ICD-9</option>
                      <option value="ICD-8">ICD-8</option>
                    </select>
                  </div>
                </div>
              )}
              {practiceMonitoringTypes.length === 0 && (
                <div className="alert alert-warning">No monitoring types configured for this practice. Please configure monitoring types in practice settings.</div>
              )}
              {errors.monitoringTypes && <div className="add-patient-error-message">{errors.monitoringTypes}</div>}
            </div>
          </div>

          <h5 className="add-patient-block-title">Insurance</h5>
          <div className="add-patient-block">
            <div className="add-patient-section-content">
              <div className="add-patient-insurance-grid">
                <div className="add-patient-insurance-label-col"></div>
                <div className="add-patient-insurance-header-col"><label>Insurance Name</label></div>
                <div className="add-patient-insurance-header-col"><label>Subscriber Id</label></div>
                <div className="add-patient-insurance-header-col"><label>Insured's Name</label></div>
                <div className="add-patient-insurance-header-col"><label>Group No.</label></div>

                {/* Primary Row */}
                <div className="add-patient-insurance-label-col label-text">Primary</div>
                <div className="add-patient-insurance-input-col">
                  <input id="primaryInsuranceName" type="text" className={`add-patient-form-control ${errors.primaryInsuranceName ? 'is-invalid' : ''}`} value={insuranceData.primary.insuranceName} onChange={(e) => handleInsuranceChange('primary', 'insuranceName', e.target.value)} />
                  {errors.primaryInsuranceName && <div className="add-patient-error-message">{errors.primaryInsuranceName}</div>}
                </div>
                <div className="add-patient-insurance-input-col">
                  <input type="text" className={`add-patient-form-control ${errors.primarySubscriberId ? 'is-invalid' : ''}`} value={insuranceData.primary.subscriberId} onChange={(e) => handleInsuranceChange('primary', 'subscriberId', e.target.value)} />
                  {errors.primarySubscriberId && <div className="add-patient-error-message">{errors.primarySubscriberId}</div>}
                </div>
                <div className="add-patient-insurance-input-col">
                  <input type="text" className={`add-patient-form-control ${errors.primaryInsuredName ? 'is-invalid' : ''}`} value={insuranceData.primary.insuredName} onChange={(e) => handleInsuranceChange('primary', 'insuredName', e.target.value)} />
                  {errors.primaryInsuredName && <div className="add-patient-error-message">{errors.primaryInsuredName}</div>}
                </div>
                <div className="add-patient-insurance-input-col">
                  <input type="text" className={`add-patient-form-control ${errors.primaryGroupNo ? 'is-invalid' : ''}`} value={insuranceData.primary.groupNo} onChange={(e) => handleInsuranceChange('primary', 'groupNo', e.target.value)} />
                  {errors.primaryGroupNo && <div className="add-patient-error-message">{errors.primaryGroupNo}</div>}
                </div>

                {/* Secondary Row */}
                <div className="add-patient-insurance-label-col label-text">Secondary</div>
                <div className="add-patient-insurance-input-col">
                  <input id="secondaryInsuranceName" type="text" className={`add-patient-form-control ${errors.secondaryInsuranceName ? 'is-invalid' : ''}`} value={insuranceData.secondary.insuranceName} onChange={(e) => handleInsuranceChange('secondary', 'insuranceName', e.target.value)} />
                  {errors.secondaryInsuranceName && <div className="add-patient-error-message">{errors.secondaryInsuranceName}</div>}
                </div>
                <div className="add-patient-insurance-input-col">
                  <input type="text" className={`add-patient-form-control ${errors.secondarySubscriberId ? 'is-invalid' : ''}`} value={insuranceData.secondary.subscriberId} onChange={(e) => handleInsuranceChange('secondary', 'subscriberId', e.target.value)} />
                  {errors.secondarySubscriberId && <div className="add-patient-error-message">{errors.secondarySubscriberId}</div>}
                </div>
                <div className="add-patient-insurance-input-col">
                  <input type="text" className={`add-patient-form-control ${errors.secondaryInsuredName ? 'is-invalid' : ''}`} value={insuranceData.secondary.insuredName} onChange={(e) => handleInsuranceChange('secondary', 'insuredName', e.target.value)} />
                  {errors.secondaryInsuredName && <div className="add-patient-error-message">{errors.secondaryInsuredName}</div>}
                </div>
                <div className="add-patient-insurance-input-col">
                  <input type="text" className={`add-patient-form-control ${errors.secondaryGroupNo ? 'is-invalid' : ''}`} value={insuranceData.secondary.groupNo} onChange={(e) => handleInsuranceChange('secondary', 'groupNo', e.target.value)} />
                  {errors.secondaryGroupNo && <div className="add-patient-error-message">{errors.secondaryGroupNo}</div>}
                </div>
              </div>
            </div>
          </div>

          <h5 id="add-patient-documents-section" className="add-patient-block-title d-inline-flex align-items-center gap-2 flex-wrap">
            {isEditMode ? 'Uploaded documents' : 'Upload documents'}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="add-patient-documents-format-tip">
                  Each file must be under 10 MB. Allowed types: PDF, Word, TXT, JPG, PNG, XML.
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="field-info-icon text-muted" style={{ cursor: 'help' }} />
            </OverlayTrigger>
          </h5>
          <div className="add-patient-block">
            {isEditMode ? (
              <div className="add-patient-section-content p-0 mb-3 add-patient-inline-table-wrap">
                <table className="table table-hover mb-0 add-patient-inline-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>File</th>
                      <th>Uploaded</th>
                      <th className="add-patient-table-actions-head">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patientDocumentsList.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">No documents uploaded yet.</td>
                      </tr>
                    ) : (
                      patientDocumentsList.map((d, idx) => (
                        <tr key={d.id != null ? `saved-doc-${d.id}` : `saved-doc-${idx}-${d.file_name || d.document || ''}`}>
                          <td>{idx + 1}</td>
                          <td>{d.document_name || d.name || '—'}</td>
                          <td className="text-break">{d.file_name || d.document || '—'}</td>
                          <td>{d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}</td>
                          <td className="add-patient-table-actions-cell">
                            <ActionButtons
                              onView={() => openPatientDocument(d)}
                              onEdit={() => handleRenamePatientDocument(d)}
                              onDelete={() => openDeleteDocumentConfirm(d)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : null}
            <div className="add-patient-section-content">
              {documents.map((doc, index) => (
                <div key={`doc-${index}`} className="add-patient-documents-row">
                  <div className="add-patient-doc-col-filename">
                    <label>File Name</label>
                    <input
                      type="text"
                      className={`add-patient-form-control ${errors[`documentFileName_${index}`] ? 'is-invalid' : ''}`}
                      value={doc.fileName}
                      onChange={(e) => updateDocument(index, 'fileName', e.target.value)}
                      placeholder="Enter file name"
                    />
                    {errors[`documentFileName_${index}`] && (
                      <div className="add-patient-error-message">{errors[`documentFileName_${index}`]}</div>
                    )}
                  </div>
                  <div className="add-patient-doc-col-upload">
                    <label>Upload File</label>
                    <div className="add-patient-upload-group-combined">
                      <input
                        type="text"
                        className={`add-patient-form-control add-patient-file-display-input ${errors[`documentFile_${index}`] || doc.fileTooLargeName ? 'is-invalid' : ''}`}
                        readOnly
                        placeholder="No file selected"
                        value={doc.file ? doc.file.name : (doc.fileTooLargeName || '')}
                      />
                      <label className="add-patient-btn-upload-right">
                        <FontAwesomeIcon icon={faCloudUploadAlt} /> Upload File
                        <input type="file" hidden multiple onChange={(e) => handleFileUpload(e, index)} accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xml" />
                      </label>
                      {index === documents.length - 1 ? (
                        <button type="button" className="add-patient-btn-icon-dark" onClick={addDocument}>
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      ) : (
                        <button type="button" className="add-patient-btn-icon-danger" onClick={() => removeDocument(index)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                    {(doc.fileTooLargeName || errors[`documentFile_${index}`]) && (
                      <div className="add-patient-error-message">
                        {errors[`documentFile_${index}`] || DOCUMENT_FILE_SIZE_ERROR}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="add-patient-terms-container">
            <input type="checkbox" id="terms" className="add-patient-terms-checkbox" checked={termsAccepted} onChange={() => { }} onClick={handleTermsClick} readOnly />
            <label htmlFor="terms" className="add-patient-terms-label-text" onClick={handleTermsClick}>Terms & Conditions <span className="add-patient-required">*</span></label>
            {errors.terms && <div className="add-patient-error-message">{errors.terms}</div>}
          </div>

          {/* Form Actions */}
          <div className="add-patient-form-actions">
            <div className="add-patient-form-actions-left">
              {isEditMode && (
                <button type="button" className="add-patient-btn add-patient-btn-logs" onClick={() => setShowLogsModal(true)}>
                  <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                  Event Logs
                </button>
              )}
            </div>
            <FormActionButtons
              onCancel={handleCancel}
              cancelDisabled={isSubmitting}
              disabled={
                isSubmitting ||
                (isEditMode && !isChanged) ||
                Boolean(errors.password) ||
                Boolean(errors.confirmPassword) ||
                hasPasswordReuseError
              }
              isSubmitting={isSubmitting}
              isEditMode={isEditMode}
            />
          </div>
        </form>
      </div>

      {/* Event Logs Modal */}
      {isEditMode && (
        <PatientsLogModal
          show={showLogsModal}
          onHide={() => setShowLogsModal(false)}
          moduleId="Patient"
          patientId={editId}
          practiceId={practiceId}
        />
      )}

      {/* Validation Warning Modal */}
      <Modal 
        show={showValidationModal} 
        onHide={() => setShowValidationModal(false)} 
        centered 
        className="add-patient-validation-modal"
      >
        <Modal.Body className="add-patient-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
          <div className="add-patient-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="add-patient-modal-icon" style={{ fontSize: '40px', color: '#1A252F' }} />
          </div>
          <h4 className="add-patient-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
          <button 
            type="button"
            className="add-patient-modal-btn" 
            onClick={() => setShowValidationModal(false)}
            style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
          >
            OK
          </button>
        </Modal.Body>
      </Modal>

      {deleteMeterConfirm && (
        <div className="practices-list-modal-overlay">
          <div className="practices-list-modal-container">
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setDeleteMeterConfirm(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body">
              <p>
                Are you sure you want to remove this meter mapping for{' '}
                <strong>
                  {[deleteMeterConfirm.product_name, deleteMeterConfirm.model_number].filter(Boolean).join(' · ') ||
                    'this device'}
                </strong>
                {deleteMeterConfirm.serial_number ? (
                  <>
                    {' '}
                    (serial <strong>{deleteMeterConfirm.serial_number}</strong>)
                  </>
                ) : null}
                ?
              </p>
              <p className="practices-list-modal-warning">This action cannot be undone.</p>
            </div>
            <div className="practices-list-modal-footer">
              <button
                type="button"
                className="practices-list-btn-cancel"
                onClick={() => setDeleteMeterConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="practices-list-btn-delete"
                onClick={handleDeleteMappedMeterConfirmed}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteDocumentConfirm && (
        <div className="practices-list-modal-overlay">
          <div className="practices-list-modal-container">
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setDeleteDocumentConfirm(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body">
              <p>
                Are you sure you want to delete the document{' '}
                <strong>
                  {deleteDocumentConfirm.document_name ||
                    deleteDocumentConfirm.name ||
                    deleteDocumentConfirm.file_name ||
                    deleteDocumentConfirm.document ||
                    'this file'}
                </strong>
                ?
              </p>
              <p className="practices-list-modal-warning">This action cannot be undone.</p>
            </div>
            <div className="practices-list-modal-footer">
              <button
                type="button"
                className="practices-list-btn-cancel"
                onClick={() => setDeleteDocumentConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="practices-list-btn-delete"
                onClick={handleDeletePatientDocumentConfirmed}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPatient;